import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Grid,
    Typography,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    Button,
    Divider,
    Autocomplete,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Snackbar,
    Radio,
    RadioGroup,
    InputAdornment,
    Tooltip,
    Chip,
    TextField
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import PrintIcon from '@mui/icons-material/Print';
import { Input } from 'components';
import { Breadcrumb } from 'components';
import PrintComponent from './PrintComponent';
import ReactQuill from 'react-quill-new';
import 'quill/dist/quill.snow.css';
import { useLocation, useNavigate } from 'react-router';
import {
    reviewOnboardRequest,
    newDefaultClientPriceList,
    getBUCodes,
    getElecodes,
    reDraft,
    actionOnboardRequest,
    paymentTermsOptionsApi,
    clientCodeExist,
    getASMBasedStateId,
    sendApprovalEmail,
    sendRejectStatusToCustomer,
    getSignature,
    getClientOnboardingStates,
    getBumaAsByState,
    getBankLetters
} from 'services';
import { ConfirmationDialog } from './ConfirmationDialog';
import {
    CONTACT_NUMBER_FIELD_MAX_LENGTH,
    FIFTY_FIELD_MAX_LENGTH,
    POSTCODE_FIELD_MAX_LENGTH,
    TEXT_FIELD_MAX_LENGTH,
    TRANSIENT_ERROR_TIMEOUT_MS,
    MAX_CREDIT_REQUIRED_LENGTH
} from '../../../auth/NewClientRegistration/steps/validationLimits';
import { useReviewHandler } from 'modules/customHooks/useReviewHandler';

interface GeneralInfo {
    id?: any;
    signatureId?: any;
    company: any;
    fullName: any;
    title: any;
    requestedDate: string;
    hasAgreedToTermsAndCondition: any;
    tandCComments: string;
    abn: any;
    companyName: string;
    eurofinsContactName: string;
    tradingName: string;
    // Business Address fields
    businessStreetNumber: string;
    businessSuburb: string;
    businessPostCode: string;
    businessCountry: string;
    businessState: string;
    // Postal Address fields
    isPostalSameAsBusiness: boolean;
    postalStreetNumber: string;
    postalSuburb: string;
    postalPostCode: string;
    postalCountry: string;
    postalState: string;
    // Contact fields
    contactName: string;
    emailAddress: string;
    phoneNumber: string;
    mobileNumber: string;
    poEmail: string;
    reviewerNotes: string;
}

interface InvoiceStatementInfo {
    contactName: string;
    email: string;
    phoneNumber: string;
    invoicePlatform: string;
    emailForStatements: string;
    suburb: string;
    state: string;
    postcode: string;
    isPoNumberMandatory: string;
    usesInvoicePlatform: string;
    needCreditAccount: string;
}

interface CreditAccountInfo {
    hasCreditReferees: string;
    id: string;
    clientOnBoardRequestID: any;
    contactName: string;
    email: string;
    position: string;
    phoneNumber: string;
    companyWebsite: string;
    natureOfBusiness: string;
    maxCreditRequired: string;
    isTrustee: string;
    directorName: string;
    dateOfIncorporation: string;
    parentCompany: string;
    previousBusinessNames: string;
}

interface CreditReferee {
    id: string;
    contactName: string;
    company: string;
    phoneNumber: string;
    email: string;
}

interface ElvisClientDetails {
    needCreditAccount: any;
    isPoNumberMandatory: any;
    invoicePlatform: any;
    code: string;
    priceList: string | number;
    isCodClient: boolean;
    ele: string;
    state: string;
    isGstExempt: boolean;
    bu: any;
    paymentTerms: string;
    isInvoiceMonthly: boolean;
    asm: string;
    isPoRequired: boolean;
    country?: any;
    isEurofinsGroup?: boolean;
    abnCheck?: boolean;
    refereeCheck?: boolean;
    reviewerComments?: string;
    bankLetterFileName: string;
}

interface FormData {
    generalInfo: GeneralInfo;
    invoiceStatementInfo: InvoiceStatementInfo;
    creditAccountInfo: CreditAccountInfo;
    creditReferees: CreditReferee[];
    elvisClientDetails: ElvisClientDetails;
    requestedContacts: any[];
}

interface Props {
    initialData?: Partial<FormData>;
    onReject?: (reason: string) => void;
}

type ContactNumberType = 'phoneNumber' | 'mobileNumber' | '';

const COMPANY_NAME_LENGTH_ERROR = 'Company Name cannot exceed 100 characters.';
const TRADING_NAME_LENGTH_ERROR = 'Trading Name cannot exceed 100 characters.';
const EUROFINS_CONTACT_LENGTH_ERROR = 'Eurofins Reference cannot exceed 200 characters';
const BUSINESS_STREET_LENGTH_ERROR = 'Business Street Name cannot exceed 100 characters.';
const BUSINESS_SUBURB_LENGTH_ERROR = 'Business Suburb cannot exceed 100 characters.';
const POSTAL_STREET_LENGTH_ERROR = 'Postal Street Name cannot exceed 100 characters.';
const POSTAL_SUBURB_LENGTH_ERROR = 'Postal Suburb cannot exceed 100 characters.';
const BUSINESS_POSTCODE_LENGTH_ERROR = 'Business PostCode cannot exceed 4 characters.';
const POSTAL_POSTCODE_LENGTH_ERROR = 'Postal Code cannot exceed 4 characters.';
const EMAIL_STATEMENTS_LENGTH_ERROR = 'Email for statements cannot exceed 50 characters.';
const EMAIL_INVOICES_LENGTH_ERROR = 'Email for Invoices cannot exceed 50 characters.';
const PO_EMAIL_LENGTH_ERROR = 'PO Email cannot exceed 100 characters.';
const PHONE_NUMBER_LENGTH_ERROR = 'Phone number cannot exceed 10 digits.';
const CONTACT_NAME_INVOICE_LENGTH_ERROR = 'Contact Name for Invoices cannot exceed 50 characters.';
const CONTACT_NAME_LENGTH_ERROR = `Contact Name cannot exceed ${FIFTY_FIELD_MAX_LENGTH} characters.`;
const EMAIL_LENGTH_ERROR = `Email Address cannot exceed ${FIFTY_FIELD_MAX_LENGTH} characters.`;
const POSITION_LENGTH_ERROR = `Position cannot exceed ${FIFTY_FIELD_MAX_LENGTH} characters.`;
const COMPANY_WEBSITE_LENGTH_ERROR = `Company Website cannot exceed ${TEXT_FIELD_MAX_LENGTH} characters.`;
const PARENT_COMPANY_LENGTH_ERROR = `Parent Company cannot exceed ${TEXT_FIELD_MAX_LENGTH} characters.`;
const PREVIOUS_BUSINESS_NAMES_LENGTH_ERROR = `Previous Business Names cannot exceed ${TEXT_FIELD_MAX_LENGTH} characters.`;
const MAX_CREDIT_REQUIRED_LENGTH_ERROR = `Maximum Credit Required cannot exceed ${MAX_CREDIT_REQUIRED_LENGTH} characters.`;
const DIRECTOR_NAME_LENGTH_ERROR = `Director Name cannot exceed ${TEXT_FIELD_MAX_LENGTH} characters.`;

const formatABN = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);

    return digits
        .replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, '$1 $2 $3 $4')
        .trim();
};

export const ReviewConfirmation: React.FC<Props> = ({
    initialData,
    onReject
}) => {
    const [showApprovalDialog, setShowApprovalDialog] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const data = location.state?.requestData || '';
    const tabValue = location.state?.tabValue;
    const id = location.state?.id;
    const isUserVersion = Boolean(location.state?.isUserVersion);
    // Utility: true if the page is opened in any view-only mode.
    const isDisabled = tabValue === 1 || tabValue === 2 || isUserVersion;
    const [formData, setFormData] = useState<FormData>({
        generalInfo: {
            companyName: '',
            abn: '',
            eurofinsContactName: '',
            tradingName: '',
            // Business Address
            businessStreetNumber: '',
            businessSuburb: '',
            businessPostCode: '',
            businessCountry: '',
            businessState: '',
            // Postal Address
            isPostalSameAsBusiness: false,
            postalStreetNumber: '',
            postalSuburb: '',
            postalPostCode: '',
            postalCountry: '',
            postalState: '',
            // Contact fields
            contactName: '',
            emailAddress: '',
            phoneNumber: '',
            mobileNumber: '',
            company: '',
            fullName: '',
            title: '',
            requestedDate: '',
            hasAgreedToTermsAndCondition: false,
            tandCComments: '',
            poEmail: '',
            reviewerNotes: '',
        },
        invoiceStatementInfo: {
            contactName: '',
            email: '',
            phoneNumber: '',
            invoicePlatform: '',
            emailForStatements: '',
            suburb: '',
            state: '',
            postcode: '',
            isPoNumberMandatory: '',
            usesInvoicePlatform: '',
            needCreditAccount: ''
        },
        creditAccountInfo: {
            contactName: '',
            email: '',
            position: '',
            phoneNumber: '',
            companyWebsite: '',
            natureOfBusiness: '',
            maxCreditRequired: '',
            isTrustee: '',
            directorName: '',
            dateOfIncorporation: '',
            parentCompany: '',
            previousBusinessNames: '',
            clientOnBoardRequestID: '',
            hasCreditReferees: '',
            id: ''
        },
        creditReferees: [],
        elvisClientDetails: {
            priceList: typeof initialData?.elvisClientDetails?.priceList === 'number'
                ? String(initialData.elvisClientDetails.priceList)
                : (initialData?.elvisClientDetails?.priceList ? String(initialData.elvisClientDetails.priceList) : '1996'),
            isCodClient: false,
            ele: '',
            state: '',
            isGstExempt: false,
            bu: '',
            paymentTerms: '',
            isInvoiceMonthly: false,
            asm: '',
            isPoRequired: false,
            needCreditAccount: '',
            isPoNumberMandatory: '',
            invoicePlatform: ''
            ,
            code: '',
            country: 'Australia',
            isEurofinsGroup: false,
            abnCheck: false,
            refereeCheck: false,
            reviewerComments: '',
            bankLetterFileName: ''
        },
        requestedContacts: [],
        ...initialData
    });

    const jwt: any = localStorage.getItem('token');
    const labUserCheck: any = jwt ? JSON.parse(atob(jwt.split('.')[1])) : null;

    const [showRejectDialog, setShowRejectDialog] = useState(false);
    const [isDefferred, setIsDefferred] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [myObCodeExists, setMyObCodeExists] = useState({
        status: false,
        message: ''
    });
    const [apiError, setApiError] = useState<{
        status: boolean;
        type: 'success' | 'info' | 'warning' | 'error';
        message: string;
    }>({
        status: false,
        type: 'error',
        message: ''
    });
    const [content, setContent] = useState<any>();
    const [lastAutoContent, setLastAutoContent] = useState('');
    const [emailList, setEmailList] = useState<any[]>([]);
    const [newPriceList, setNewPriceList] = useState<any>([]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [signatureImage, setSignatureImage] = useState('');
    const [generalContactNumberType, setGeneralContactNumberType] = useState<ContactNumberType>('');
    const [postcodeErrors, setPostcodeErrors] = useState<Record<string, string>>({});
    const postcodeErrorTimeouts = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});
    const [bankLetterOptions, setBankLetterOptions] = useState<any[]>([]);

    const { handleReview } = useReviewHandler();

    const getCompanyNameValidationMessage = () => {
        const companyName = String(formData?.generalInfo?.companyName || '').trim();

        if (!companyName) return 'Company Name is required';

        return companyName.length > TEXT_FIELD_MAX_LENGTH ? COMPANY_NAME_LENGTH_ERROR : '';
    };

    const getTradingNameValidationMessage = () => {
        const tradingName = String(formData?.generalInfo?.tradingName || '').trim();

        if (!tradingName) return 'Trading Name is required';

        return tradingName.length > TEXT_FIELD_MAX_LENGTH ? TRADING_NAME_LENGTH_ERROR : '';
    };

    const getEurofinsContactNameValidationMessage = () => {
        const eurofinsContactName = String(formData?.generalInfo?.eurofinsContactName || '').trim();
        if (!eurofinsContactName) return 'Eurofins Contact Name is required';

        return eurofinsContactName.length > TEXT_FIELD_MAX_LENGTH ? EUROFINS_CONTACT_LENGTH_ERROR : '';
    };

    const getContactNameValidationMessage = () => {
        const contactName = String(formData?.generalInfo?.contactName || '').trim();

        if (!contactName) return 'Contact Name is required';

        return contactName.length > FIFTY_FIELD_MAX_LENGTH ? CONTACT_NAME_LENGTH_ERROR : '';
    };

    const getBusinessStreetValidationMessage = () => {
        const businessStreet = String(formData?.generalInfo?.businessStreetNumber || '').trim();

        // if(!businessStreet) return 'Business Street is required';

        return businessStreet.length > TEXT_FIELD_MAX_LENGTH ? BUSINESS_STREET_LENGTH_ERROR : '';
    };

    const getBusinessSuburbValidationMessage = () => {
        const businessSuburb = String(formData?.generalInfo?.businessSuburb || '').trim();

        // if(!businessSuburb) return 'Business Suburb is required';

        return businessSuburb.length > TEXT_FIELD_MAX_LENGTH ? BUSINESS_SUBURB_LENGTH_ERROR : '';
    };

    const getBusinessPostCodeValidationMessage = () => {
        if (postcodeErrors.businessPostCode) {
            return postcodeErrors.businessPostCode;
        }

        const businessPostCode = String(formData?.generalInfo?.businessPostCode || '').trim();

        // if(!businessPostCode) return 'Business Post Code is required';

        return businessPostCode.length > POSTCODE_FIELD_MAX_LENGTH ? BUSINESS_POSTCODE_LENGTH_ERROR : '';
    };

    const getPostalStreetValidationMessage = () => {
        const postalStreet = String(formData?.generalInfo?.postalStreetNumber || '').trim();

        // if(!postalStreet) return 'Postal Street is required';

        return postalStreet.length > TEXT_FIELD_MAX_LENGTH ? POSTAL_STREET_LENGTH_ERROR : '';
    };

    const getPostalSuburbValidationMessage = () => {
        const postalSuburb = String(formData?.generalInfo?.postalSuburb || '').trim();

        // if(!postalSuburb) return 'Postal Suburb is required';

        return postalSuburb.length > TEXT_FIELD_MAX_LENGTH ? POSTAL_SUBURB_LENGTH_ERROR : '';
    };

    const getPostalPostCodeValidationMessage = () => {
        if (postcodeErrors.postalPostCode) {
            return postcodeErrors.postalPostCode;
        }

        const postalPostCode = String(formData?.generalInfo?.postalPostCode || '').trim();

        // if(!postalPostCode) return 'Postal Post Code is required';

        return postalPostCode.length > POSTCODE_FIELD_MAX_LENGTH ? POSTAL_POSTCODE_LENGTH_ERROR : '';
    };

    const getEmailForStatementsValidationMessage = () => {
        const emailForStatements = String(formData?.invoiceStatementInfo?.emailForStatements || '').trim();

        // if(!emailForStatements) return 'Email for Statements is required';

        return emailForStatements.length > FIFTY_FIELD_MAX_LENGTH ? EMAIL_STATEMENTS_LENGTH_ERROR : '';
    };

    const getEmailForInvoicesValidationMessage = () => {
        const emailForInvoices = String(formData?.invoiceStatementInfo?.email || '').trim();

        // if(!emailForInvoices) return 'Email for Invoices is required';

        return emailForInvoices.length > FIFTY_FIELD_MAX_LENGTH ? EMAIL_INVOICES_LENGTH_ERROR : '';
    };

    const getPoEmailValidationMessage = () => {
        const poEmail = String(formData?.generalInfo?.poEmail || '').trim();

        // if(!poEmail) return 'PO Email is required';

        return poEmail.length > TEXT_FIELD_MAX_LENGTH ? PO_EMAIL_LENGTH_ERROR : '';
    };

    const getInvoicePhoneValidationMessage = () => {
        const phoneNumber = String(formData?.invoiceStatementInfo?.phoneNumber || '').trim();

        return phoneNumber.length > CONTACT_NUMBER_FIELD_MAX_LENGTH ? PHONE_NUMBER_LENGTH_ERROR : '';
    };

    const getContactNameForInvoicesValidationMessage = () => {
        const contactName = String(formData?.invoiceStatementInfo?.contactName || '').trim();

        if (!contactName) return "Contact Name for Invoices is required";

        if (contactName.length > FIFTY_FIELD_MAX_LENGTH) {
            return CONTACT_NAME_INVOICE_LENGTH_ERROR;
        }

        return "";
    };

    const getCreditAccountCompanyWebsiteValidationMessage = () => {
        const companyWebsite = String(formData?.creditAccountInfo?.companyWebsite || '').trim();

        // if(!companyWebsite && isCreditAccountDisabled) return 'Company Website is required';

        return companyWebsite.length > TEXT_FIELD_MAX_LENGTH ? COMPANY_WEBSITE_LENGTH_ERROR : '';
    };

    const getCreditAccountPhoneValidationMessage = () => {
        const phoneNumber = String(formData?.creditAccountInfo?.phoneNumber || '').trim();

        // if(!phoneNumber && isCreditAccountDisabled) return 'Phone Number is required';

        return phoneNumber.length > CONTACT_NUMBER_FIELD_MAX_LENGTH ? PHONE_NUMBER_LENGTH_ERROR : '';
    };

    const getCreditAccountContactNameValidationMessage = () => {
        const contactName = String(formData?.creditAccountInfo?.contactName || '').trim();

        // if(!contactName && isCreditAccountDisabled) return 'Contact Name is required';

        return contactName.length > FIFTY_FIELD_MAX_LENGTH ? CONTACT_NAME_LENGTH_ERROR : '';
    };

    const getCreditAccountEmailValidationMessage = () => {
        const email = String(formData?.creditAccountInfo?.email || "").trim();

        // Don't validate if credit account isn't required
        if (!isCreditRequired || !email) {
            return "";
        }

        if (email.length > FIFTY_FIELD_MAX_LENGTH) {
            return EMAIL_LENGTH_ERROR;
        }

        if (!isValidEmail(email)) {
            return "Invalid email format";
        }

        return "";
    };

    const getCreditAccountPositionValidationMessage = () => {
        const position = String(formData?.creditAccountInfo?.position || '').trim();

        // if(!position && isCreditAccountDisabled) return 'Position is required';

        return position.length > FIFTY_FIELD_MAX_LENGTH ? POSITION_LENGTH_ERROR : '';
    };

    const getCreditAccountDirectorNameValidationMessage = () => {
        const directorName = String(formData?.creditAccountInfo?.directorName || '').trim();

        // if(!directorName && isCreditAccountDisabled) return 'Director Name is required';

        return directorName.length > TEXT_FIELD_MAX_LENGTH ? DIRECTOR_NAME_LENGTH_ERROR : '';
    };

    const getCreditAccountParentCompanyValidationMessage = () => {
        const parentCompany = String(formData?.creditAccountInfo?.parentCompany || '').trim();

        return parentCompany.length > TEXT_FIELD_MAX_LENGTH ? PARENT_COMPANY_LENGTH_ERROR : '';
    };

    const getCreditAccountPreviousBusinessNamesValidationMessage = () => {
        const previousBusinessNames = String(formData?.creditAccountInfo?.previousBusinessNames || '').trim();

        return previousBusinessNames.length > TEXT_FIELD_MAX_LENGTH ? PREVIOUS_BUSINESS_NAMES_LENGTH_ERROR : '';
    };

    const getCreditAccountMaxCreditRequiredValidationMessage = () => {
        const maxCreditRequired = String(formData?.creditAccountInfo?.maxCreditRequired || '').trim();

        return maxCreditRequired.length > MAX_CREDIT_REQUIRED_LENGTH ? MAX_CREDIT_REQUIRED_LENGTH_ERROR : '';
    };

    const getCreditRefereePhoneValidationMessage = (phoneNumber) => {
        const value = String(phoneNumber || '').trim();

        if (!value) {
            return 'Phone Number is required';
        }

        if (value.length > CONTACT_NUMBER_FIELD_MAX_LENGTH) {
            return PHONE_NUMBER_LENGTH_ERROR;
        }

        return '';
    };

    const getDefaultPriceListId = () => {
        if (!Array.isArray(newPriceList) || newPriceList.length === 0) {
            return 1996;
        }

        const defaultOption = newPriceList.find((option: any) => option.id === 1996) || newPriceList[0];
        return defaultOption ? Number(defaultOption.id) : 1996;
    };

    const getResolvedPriceListId = (value: any) => {
        if (value !== null && value !== undefined && String(value) !== '') {
            return Number(value);
        }

        return getDefaultPriceListId();
    };

    // Utility: Disable fields based on edit mode
    const isFieldsDisabled = isDisabled || !isEditMode;
    const isCreditAccountDisabled = isDisabled || !isEditMode || (formData?.invoiceStatementInfo?.needCreditAccount === 'no' || formData?.creditAccountInfo === null);

    useEffect(() => {
        if (generalContactNumberType) {
            return;
        }

        const requestedContact = formData.requestedContacts?.[0];
        const phoneNumber = String(requestedContact?.phoneNumber || formData.generalInfo.phoneNumber || '').trim();
        const mobileNumber = String(requestedContact?.mobileNumber || formData.generalInfo.mobileNumber || '').trim();

        if (phoneNumber) {
            setGeneralContactNumberType('phoneNumber');
            return;
        }

        if (mobileNumber) {
            setGeneralContactNumberType('mobileNumber');
        }
    }, [formData.generalInfo.mobileNumber, formData.generalInfo.phoneNumber, formData.requestedContacts, generalContactNumberType]);

    // Ensure priceList is always a valid option
    useEffect(() => {
        if (newPriceList && newPriceList?.length > 0) {
            setFormData(prev => {
                const exists = newPriceList?.some(opt => opt.id === Number(prev.elvisClientDetails?.priceList));
                if (!exists) {
                    return {
                        ...prev,
                        elvisClientDetails: {
                            ...prev.elvisClientDetails,
                            priceList: getDefaultPriceListId()
                        }
                    };
                }
                return prev;
            });
        }
    }, [newPriceList]);
    const [eleCodes, setEleCodes] = useState<any>([]);
    const [buCodes, setBuCodes] = useState<any>([]);
    const [paymentTermsOptions, setPaymentTermsOptions] = useState<any>([]);
    const [asmOptions, setAsmOptions] = useState<any>([]);
    const [australianStates, setAustralianStates] = useState<any>([]);
    const [stateAsmOptions, setStateAsmOptions] = useState<any>([]);
    const [selectedAsmState, setSelectedAsmState] = useState('');
    const getApprovalRecipientLabel = (recipient: any) => {
        if (!recipient) {
            return '';
        }

        if (typeof recipient === 'string') {
            return recipient.trim();
        }

        return String(recipient.bumaName || recipient.name || '').trim();
    };

    const approvalGreetingName = emailList.length > 1
        ? 'All'
        : (getApprovalRecipientLabel(emailList[0]));

    const businessName = formData?.generalInfo?.companyName || '';
    const contactName = formData?.generalInfo?.contactName || '';
    const emailAddress = formData?.generalInfo?.emailAddress || '';
    const elvisConnectUrl = window.location.origin;

    const defaultText = `<p>Hi ${approvalGreetingName},</p><br/><p>Please log in to <a href="${elvisConnectUrl}/home/new-client-request" target="_blank"> ELVIS Connect </a> and confirm further details as requested for the client setup to be completed.</p> <br/> <p><b>Client Details:</b></p> <p>Business Name: <b>${businessName}</b></p> <p>Contact Name: <b>${contactName}</b></p> <p>Email: <b>${emailAddress}</b></p> <br/><p>If any updates are required, please make the necessary changes in the system.</p><br/><p>Please don’t hesitate to reach out if you have any questions, thank you.</p><br/> <p> Kind Regards,<br/>${labUserCheck?.given_name} - AR Team</p>`;

    // determine role flags: prefer explicit role in location.state, otherwise infer ASM presence from data
    const isASM = labUserCheck.Permission.includes("ReviewOnboardRequest");
    const isOfficeAdmin = labUserCheck.Permission.includes("ActionOnboardRequest");
    const isBothPermissions = isASM && isOfficeAdmin;
    const isASMGridSize = (isBothPermissions || isOfficeAdmin) ? 6 : 12;
    // Country and State options
    const countryOptions = [
        'Australia'
    ];

    useEffect(() => {
        getClientOnboardingStates().then((res: any) => {
            if (res.status === 200) {
                setAustralianStates(res?.data || []);
            }
        });
    }, []);

    useEffect(() => {
        if (selectedAsmState) {
            getBumaAsByState(selectedAsmState).then((res: any) => {
                if (res.status === 200) {
                    setStateAsmOptions(res?.data || []);
                }
            });
        }
    }, [selectedAsmState]);

    const getStateOptions = (country: string) => {
        switch (country) {
            case 'Australia':
                return australianStates;
            default:
                return [];
        }
    };

    const getAustralianStateValue = (value: any) => {
        if (value === null || value === undefined || value === '') {
            return '';
        }

        const stateOption = australianStates.find((option: any) => (
            option?.value === value
            || option?.name === value
            || option?.displayName === value
            || String(option?.id) === String(value)
        ));

        return stateOption?.value || String(value);
    };

    useEffect(() => {
        if (labUserCheck?.UserType !== 'Internal') {
            return;
        }

        if (content === undefined || content === lastAutoContent) {
            setContent(defaultText);
            setLastAutoContent(defaultText);
        }
    }, [content, defaultText, labUserCheck?.UserType, lastAutoContent]);

    useEffect(() => {
        if (labUserCheck?.UserType === 'Internal') {
            newDefaultClientPriceList().then((res: any) => {
                setNewPriceList(res?.data);
            })

            getElecodes().then((res: any) => {
                setEleCodes(res?.data);
            })

            paymentTermsOptionsApi().then((res: any) => {
                setPaymentTermsOptions(res?.data);
            });
        }
    }, [])

    useEffect(() => {
        if (formData?.elvisClientDetails?.state) {
            getASMOptions(formData?.elvisClientDetails?.state);
        }
    }, [formData?.elvisClientDetails?.state]);

    const getASMOptions = (stateId: string) => {
        if (stateId) {
            getASMBasedStateId(stateId).then((res: any) => {
                if (res?.status === 200) {
                    setAsmOptions(res.data);
                } else {
                    setAsmOptions([]);
                }
            }).catch((error) => {
                const errorMessage = error?.response?.data || error?.message || 'An error occurred while fetching ASM options.';
                setApiError({
                    status: true,
                    type: 'error',
                    message: errorMessage
                });
                setAsmOptions([]);
            });
        }
    };

    const handleEditorChange = (html: any) => {
        setContent(html);
    }

    useEffect(() => {
        if (data) {
            const clientRequest = data.clientOnboardRequest || {};
            const creditAccount = data.creditAccountDetail || {};
            const creditRefereesList = data.creditReferees || [];
            const requestedContactsList = data.requestedContacts || [];

            // Find default contact (general info contact)
            const defaultContact = requestedContactsList.find((c: any) => c.isDefault) || {};
            // Find invoice contact
            const invoiceContact = requestedContactsList.find((c: any) => c.isInvoice && !c.isDefault) || defaultContact;

            setFormData(prev => ({
                ...prev,
                generalInfo: {
                    companyName: clientRequest?.companyName || '',
                    abn: clientRequest?.abn || '',
                    eurofinsContactName: clientRequest?.eurofinsContactName || '',
                    tradingName: clientRequest?.tradingName || '',
                    // Business Address
                    businessStreetNumber: clientRequest?.business_StreetName || '',
                    businessSuburb: clientRequest?.business_Suburb || '',
                    businessPostCode: clientRequest?.business_PostCode || '',
                    businessCountry: clientRequest?.business_Country || '',
                    businessState: getAustralianStateValue(clientRequest?.business_State),
                    // Postal Address
                    isPostalSameAsBusiness: clientRequest?.isPostalAddressSameAsBusinessAddress || false,
                    postalStreetNumber: clientRequest?.postal_StreetName || '',
                    postalSuburb: clientRequest?.postal_Suburb || '',
                    postalPostCode: clientRequest?.postal_PostCode || '',
                    postalCountry: clientRequest?.postal_Country || '',
                    postalState: getAustralianStateValue(clientRequest?.postal_State),
                    // Contact fields
                    contactName: defaultContact?.name || '',
                    emailAddress: defaultContact?.email || '',
                    phoneNumber: defaultContact?.phoneNumber || '',
                    mobileNumber: defaultContact?.mobileNumber || '',
                    hasAgreedToTermsAndCondition: clientRequest?.hasAgreedToTermsAndCondition || false,
                    tandCComments: clientRequest?.tandCComments || '',
                    title: clientRequest?.title || '',
                    fullName: clientRequest?.fullName || '',
                    company: clientRequest?.company || '',
                    requestedDate: clientRequest?.requestedDate
                        ? clientRequest.requestedDate.split('T')[0]
                        : '',
                    id: clientRequest?.id || '',
                    signatureId: clientRequest?.signatureId || '',
                    poEmail: clientRequest?.poEmail || '',
                    reviewerNotes: clientRequest?.reviewerNotes || ''
                },
                invoiceStatementInfo: {
                    contactName: invoiceContact?.name || '',
                    email: invoiceContact?.email || '',
                    phoneNumber: invoiceContact?.phoneNumber || '',
                    invoicePlatform: clientRequest?.invoicePlatform || '',
                    emailForStatements: invoiceContact?.emailForStatements || '',
                    suburb: '',
                    state: '',
                    postcode: '',
                    isPoNumberMandatory: clientRequest?.isPONumberMandatory ? 'yes' : 'no',
                    usesInvoicePlatform: clientRequest?.hasInvoicePlatform ? 'yes' : 'no',
                    needCreditAccount: clientRequest?.needCreditAccount ? 'yes' : 'no'
                },
                creditAccountInfo: (creditAccount !== null && Object.keys(creditAccount).length > 0) ? {
                    contactName: creditAccount?.contactName,
                    email: creditAccount?.email,
                    position: creditAccount?.position,
                    phoneNumber: creditAccount?.phoneNumber,
                    companyWebsite: creditAccount?.companyWebsite,
                    natureOfBusiness: creditAccount?.natureOfBusiness,
                    maxCreditRequired: creditAccount?.maxCreditRequired,
                    isTrustee: creditAccount?.isTrustee ? 'yes' : 'no',
                    directorName: creditAccount?.directorName,
                    dateOfIncorporation: creditAccount?.dateOfIncorporation
                        ? creditAccount.dateOfIncorporation.split('T')[0]
                        : '',
                    parentCompany: creditAccount?.parentCompany,
                    previousBusinessNames: creditAccount?.previousBusiness,
                    clientOnBoardRequestID: creditAccount?.clientOnBoardRequestID,
                    id: creditAccount?.id,
                    hasCreditReferees: creditAccount?.hasCreditReferees ? 'yes' : 'no',
                } : null,
                creditReferees: Object.keys(creditAccount).length > 0 ? creditRefereesList.map((referee: any, index: number) => ({
                    id: referee?.id,
                    contactName: referee?.contactName || referee?.name || '',
                    company: referee?.company,
                    phoneNumber: referee?.phoneNumber,
                    email: referee?.email,
                    creditAccountID: referee?.creditAccountID || creditAccount?.id || ''
                })) : [],
                elvisClientDetails: {
                    ...prev.elvisClientDetails,
                    code: clientRequest?.proposeClientCode || '',
                    priceList: getResolvedPriceListId(clientRequest?.proposedPriceListID),
                    ele: clientRequest?.proposedELECode || '',
                    state: clientRequest?.proposedReportStateID || '',
                    country: clientRequest?.proposedCountry || '',
                    isGstExempt: clientRequest?.proposedIsGSTExempt || false,
                    bu: clientRequest?.proposedBUCode || '',
                    paymentTerms: clientRequest?.proposedPaymentTerms,
                    isInvoiceMonthly: clientRequest?.proposedIsMonthlyInvoice || false,
                    asm: clientRequest?.proposedASM || '',
                    isPoRequired: clientRequest?.proposedIsPORequired || false,
                    isEurofinsGroup: clientRequest?.proposedIsEurofinsGroup || false,
                    abnCheck: clientRequest?.abnCheck || false,
                    refereeCheck: clientRequest?.refereeCheck || false,
                    naCheck: clientRequest?.naCheck || false,
                    reviewerComments: clientRequest?.reviewerComments || '',
                    bankLetterFileName: clientRequest?.bankLetterFileName || ''
                },
                requestedContacts: requestedContactsList
            }));
            getSignatureImage(clientRequest?.signatureId);
            fetchBUCodes(clientRequest?.proposedELECode);
        }
    }, [australianStates, data, newPriceList]);


    const toolbarOptions = [
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'list': 'check' }],
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'align': [] }]
    ];

    const modules = {
        toolbar: {
            container: toolbarOptions,
        },
        clipboard: {
            matchVisual: false,
        },
    };

    const handleInputChange = (section: keyof FormData, field: string, value: any) => {
        if (section === 'generalInfo' && (field === 'businessPostCode' || field === 'postalPostCode')) {
            const postcodeValue = String(value ?? '');
            const errorField = field;
            const postcodeMaxLength = POSTCODE_FIELD_MAX_LENGTH;
            const postcodeErrorMessage = field === 'businessPostCode'
                ? BUSINESS_POSTCODE_LENGTH_ERROR
                : POSTAL_POSTCODE_LENGTH_ERROR;

            if (postcodeValue.length > postcodeMaxLength) {
                const existingTimeout = postcodeErrorTimeouts.current[errorField];
                if (existingTimeout) {
                    clearTimeout(existingTimeout);
                }

                setPostcodeErrors(prev => ({
                    ...prev,
                    [errorField]: postcodeErrorMessage
                }));

                postcodeErrorTimeouts.current[errorField] = setTimeout(() => {
                    setPostcodeErrors(prev => {
                        if (prev[errorField] !== postcodeErrorMessage) {
                            return prev;
                        }

                        const nextErrors = { ...prev };
                        delete nextErrors[errorField];
                        return nextErrors;
                    });
                    postcodeErrorTimeouts.current[errorField] = null;
                }, TRANSIENT_ERROR_TIMEOUT_MS);

                return;
            }

            const existingTimeout = postcodeErrorTimeouts.current[errorField];
            if (existingTimeout) {
                clearTimeout(existingTimeout);
                postcodeErrorTimeouts.current[errorField] = null;
            }

            setPostcodeErrors(prev => {
                if (!prev[errorField]) {
                    return prev;
                }

                const nextErrors = { ...prev };
                delete nextErrors[errorField];
                return nextErrors;
            });
        }

        if (section === 'creditReferees') {
            const [refereeId, refereeField] = field.split('.');
            setFormData(prev => ({
                ...prev,
                creditReferees: prev.creditReferees.map(ref =>
                    ref.id === refereeId ? { ...ref, [refereeField]: value } : ref
                )
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: value
                }
            }));
        }
    };

    useEffect(() => {
        return () => {
            Object.values(postcodeErrorTimeouts.current).forEach((timeoutId) => {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
            });
        };
    }, []);

    const fetchBUCodes = (eleCode: string) => {
        if (eleCode) {
            getBUCodes(eleCode).then((res: any) => {
                setBuCodes(res?.data || []);
            }).catch((error) => {
                setBuCodes([]);
            });
        } else {
            setBuCodes([]);
        }
    };

    const getSignatureImage = (signatureId: any) => {
        if (signatureId) {
            getSignature(signatureId).then((res: any) => {
                if (res?.data && res.data instanceof Blob && res.data.size > 0) {
                    const imageUrl = URL.createObjectURL(new Blob([res.data], { type: 'image/jpeg' }));
                    setSignatureImage(imageUrl);
                } else {
                    setSignatureImage('');
                }
            }).catch((error) => {
                setSignatureImage('');
            });
        }
    }

    useEffect(() => {
        getBankLetterLabel();
    }, []);

    const getBankLetterLabel = () => {
        try {
            getBankLetters().then((res: any) => {
                setBankLetterOptions(res?.data || []);
            }).catch((error) => {
                setBankLetterOptions([]);
            });
        } catch (error) {
            setBankLetterOptions([]);
        }
    }

    // Helper functions to get label from value
    const getPriceListLabel = (value: any) => {
        if (!value) return '-';
        const item = newPriceList?.find((opt: any) => opt.id === Number(value) || opt.value === value);
        return item?.name;
    };

    const getASMLabel = (value: any) => {
        if (!value) return '-';
        const item = asmOptions?.find((opt: any) => opt.userID === Number(value) || opt.asmName === value || opt.asmEmail === value);
        return item?.asmName || item?.asmEmail || '-';
    };

    const getBULabel = (value: any) => {
        if (!value) return '-';
        const item = buCodes.find((opt: any) => opt.id === Number(value) || opt.value === value || opt.code === value || opt === value);
        return item || value;
    };

    const getPaymentTermsLabel = (value: any) => {
        if (value === null || value === undefined || value === '') return '-';
        const item = paymentTermsOptions.find((opt: any) => opt.id === Number(value) || opt.value === value);
        return item?.description || '-';
    };

    const getElvisCountryValue = (value: any) => {
        return value && value !== 'Australia' ? 'Overseas' : 'Australia';
    };

    const getStateLabel = (value: any) => {
        if (!value) return '-';
        const stateOption = australianStates.find((option: any) => (
            String(option?.id) === String(value)
            || option?.value === value
            || option?.name === value
        ));
        return stateOption?.name || stateOption?.displayName || String(value);
    };

    const getEleLabel = (value: any) => {
        if (!value) return '-';
        const item = eleCodes.find((opt: any) => opt === value);
        return item || value;
    }

    const handleSelectChange = (field: string, event: SelectChangeEvent<string | number>) => {
        const value = event.target.value;
        if (field === 'country') {
            handleInputChange('elvisClientDetails', field, String(value));
        } else if (field === 'state') {
            handleInputChange('elvisClientDetails', field, String(value));
        } else if (field === 'ele') {
            handleInputChange('elvisClientDetails', field, String(value));
            // Reset BU field when ELE changes
            handleInputChange('elvisClientDetails', 'bu', '');
            // Fetch BU codes based on selected ELE code
            fetchBUCodes(String(value));
        } else {
            handleInputChange('elvisClientDetails', field, value);
        }
    };

    const handleAddressSelectChange = (section: 'business' | 'postal', field: string, value: string) => {
        if (section === 'business') {
            handleInputChange('generalInfo', `business${field.charAt(0).toUpperCase() + field.slice(1)}`, value);
            if (field === 'Country') {
                handleInputChange('generalInfo', 'businessState', '');
            }
        } else {
            handleInputChange('generalInfo', `postal${field.charAt(0).toUpperCase() + field.slice(1)}`, value);
            if (field === 'Country') {
                handleInputChange('generalInfo', 'postalState', '');
            }
        }
    };

    const handleRadioChange = (field: string, value: string) => {
        handleInputChange('invoiceStatementInfo', field, value);
    };

    const handleTrusteeChange = (value: string) => {
        const newValue = formData.creditAccountInfo.isTrustee === value ? '' : value;
        handleInputChange('creditAccountInfo', 'isTrustee', newValue);
    };

    const getGeneralContactNumberValue = () => {
        if (generalContactNumberType === 'mobileNumber') {
            return formData.requestedContacts[0]?.mobileNumber || formData.generalInfo.mobileNumber || '';
        }

        if (generalContactNumberType === 'phoneNumber') {
            return formData.requestedContacts[0]?.phoneNumber || formData.generalInfo.phoneNumber || '';
        }

        return '';
    };

    const updateGeneralContactNumbers = (phoneNumber: string, mobileNumber: string) => {
        setFormData(prev => {
            const updatedContacts = [...prev.requestedContacts];

            if (updatedContacts[0]) {
                updatedContacts[0] = {
                    ...updatedContacts[0],
                    phoneNumber,
                    mobileNumber
                };
            }

            return {
                ...prev,
                generalInfo: {
                    ...prev.generalInfo,
                    phoneNumber,
                    mobileNumber
                },
                requestedContacts: updatedContacts
            };
        });
    };

    const handleGeneralContactNumberTypeChange = (value: ContactNumberType) => {
        const currentValue = getGeneralContactNumberValue();

        setGeneralContactNumberType(value);

        if (value === 'phoneNumber') {
            updateGeneralContactNumbers(currentValue, '');
            return;
        }

        if (value === 'mobileNumber') {
            updateGeneralContactNumbers('', currentValue);
            return;
        }

        updateGeneralContactNumbers('', '');
    };

    const handleGeneralContactNumberChange = (value: string) => {
        if (generalContactNumberType === 'phoneNumber') {
            updateGeneralContactNumbers(value, '');
            return;
        }

        if (generalContactNumberType === 'mobileNumber') {
            updateGeneralContactNumbers('', value);
        }
    };

    const handlePostalAddressChange = (checked: boolean) => {
        const updatedData = { ...formData };
        updatedData.generalInfo.isPostalSameAsBusiness = checked;

        if (checked) {
            updatedData.generalInfo.postalStreetNumber = formData.generalInfo.businessStreetNumber;
            updatedData.generalInfo.postalSuburb = formData.generalInfo.businessSuburb;
            updatedData.generalInfo.postalPostCode = formData.generalInfo.businessPostCode;
            updatedData.generalInfo.postalCountry = formData.generalInfo.businessCountry;
            updatedData.generalInfo.postalState = getAustralianStateValue(formData.generalInfo.businessState);
        } else {
            updatedData.generalInfo.postalStreetNumber = '';
            updatedData.generalInfo.postalSuburb = '';
            updatedData.generalInfo.postalPostCode = '';
            updatedData.generalInfo.postalCountry = '';
            updatedData.generalInfo.postalState = '';
        }

        setFormData(updatedData);
    };

    const sendApproval = (status: any) => {
        // Generate HTML content with inline paragraph styles
        const htmlContent = `
${content ? content : ''}
        `;

        // Prepare the payload for the API
        const emailPayload = {
            toList: emailList,
            content: htmlContent
        };

        // Send the email
        sendApprovalEmail(emailPayload).then((res: any) => {
            if (res.status === 200) {
                setAlertMessage('Approval email sent successfully!');
                setShowValidationErrors(false);
                setShowSuccessAlert(true);
                setShowApprovalDialog(false);

                setTimeout(() => {
                    navigate('/home/new-client-request');
                }, 1500);
            } else {
                setAlertMessage(res.data || 'Failed to send approval email');
                setShowValidationErrors(true);
                setShowSuccessAlert(true);
            }
        }).catch((error) => {
            const errorMessage = error?.response?.data ||
                error?.response?.data?.error ||
                error?.message ||
                'An error occurred while sending the approval email';

            setAlertMessage(errorMessage);
            setShowValidationErrors(true);
            setShowSuccessAlert(true);
        });
    };

    const handleReject = (deferred = false) => {
        setShowValidationErrors(false);
        setIsDefferred(deferred);
        setShowRejectDialog(true);
    };

    const getFullNameValidationMessage = () => {
        const value = formData.generalInfo.fullName;

        if (!value?.trim()) {
            return "Full Name is required";
        }

        if (value.trim().length > 100) {
            return "Full Name must be within 100 characters";
        }

        return "";
    };

    const getCompanyValidationMessage = () => {
        const value = formData.generalInfo.company;

        if (!value?.trim()) {
            return "Company is required";
        }

        if (value.trim().length > 100) {
            return "Company must be within 100 characters";
        }

        return "";
    };

    const getTitleValidationMessage = () => {
        const value = formData.generalInfo.title;

        if (!value?.trim()) {
            return "Position Title is required";
        }

        if (value.trim().length > 100) {
            return "Position Title must be within 100 characters";
        }

        return "";
    };

    const getGeneralContactNumberValidationMessage = () => {
        if (!showValidationErrors) return "";

        const value = getGeneralContactNumberValue();

        const fieldName =
            generalContactNumberType === "phoneNumber"
                ? "Phone Number"
                : "Mobile Number";

        // Required validation
        if (!value?.trim()) {
            return `${fieldName} is required`;
        }

        // Numeric validation
        if (!/^[0-9]+$/.test(value)) {
            return `${fieldName} must contain only numbers`;
        }

        // Maximum length validation
        if (value.length > 10) {
            return `${fieldName} must not exceed 10 digits`;
        }

        return "";
    };

    const validateForm = async () => {
        setShowValidationErrors(true);

        const generalValid = validateGeneralInfoSection();

        if (!generalValid) {
            setAlertMessage("Please complete General Information section");
            setShowSuccessAlert(true);
            return false;
        }

        const invoiceValidationMessage = validateInvoiceStatementSection();

        if (invoiceValidationMessage) {
            setAlertMessage(invoiceValidationMessage);
            setShowSuccessAlert(true);
            return false;
        }

        const creditValid = validateCreditAccountSection();

        if (!creditValid) {
            setAlertMessage("Please complete Credit Account section");
            setShowSuccessAlert(true);
            return false;
        }

        const signatureValid = validateSignatureSection();

        if (!signatureValid) {
            setAlertMessage("Please complete Signature section correctly");
            setShowSuccessAlert(true);
            return false;
        }

        return true;
    };

    const validateGeneralInfoSection = () => {
        const general = formData.generalInfo;

        // Required fields
        if (!general.companyName?.trim()) return false;
        if (!general.tradingName?.trim()) return false;
        if (!general.contactName?.trim()) return false;

        // ABN validation
        const abn = general.abn?.replace(/\D/g, "");

        const paymentTerms = formData?.elvisClientDetails?.paymentTerms;
        const isPaymentTermsZero = String(paymentTerms) === '0';

        if (!abn) {
            if (!isPaymentTermsZero) return false; // ABN required if paymentTerms is not 0
        } else {
            if (abn.length !== 11) return false; // Validate entered ABN always
        }

        // Contact Number validation
        const contactNumber = getGeneralContactNumberValue();

        if (!contactNumber?.trim()) return false;

        if (!/^[0-9]+$/.test(contactNumber)) return false;

        if (contactNumber.length > 10) return false;


        // Business Address validation
        if (!general.businessStreetNumber?.trim()) return false;
        if (!general.businessSuburb?.trim()) return false;
        if (!general.businessPostCode?.trim()) return false;
        if (!general.businessCountry?.trim()) return false;
        if (!general.businessState?.trim()) return false;


        // Existing validation messages
        if (getCompanyNameValidationMessage()) return false;
        if (getTradingNameValidationMessage()) return false;
        if (getContactNameValidationMessage()) return false;
        if (getEurofinsContactNameValidationMessage()) return false;

        if (getGeneralContactNumberValidationMessage()) return false;

        if (getBusinessStreetValidationMessage()) return false;
        if (getBusinessSuburbValidationMessage()) return false;
        if (getBusinessPostCodeValidationMessage()) return false;


        // Postal Address validation
        if (!general.isPostalSameAsBusiness) {

            if (!general.postalStreetNumber?.trim()) return false;
            if (!general.postalSuburb?.trim()) return false;
            if (!general.postalPostCode?.trim()) return false;
            if (!general.postalCountry?.trim()) return false;
            if (!general.postalState?.trim()) return false;


            // Postal Street max length
            if (general.postalStreetNumber.trim().length > 50) {
                return false;
            }


            // Postal Suburb max length
            if (general.postalSuburb.trim().length > 50) {
                return false;
            }


            // Postal Postcode validation
            const postalPostCode = general.postalPostCode.trim();

            if (!/^[0-9]+$/.test(postalPostCode)) {
                return false;
            }

            if (postalPostCode.length !== 4) {
                return false;
            }


            // Existing postal validation messages
            if (getPostalStreetValidationMessage()) return false;
            if (getPostalSuburbValidationMessage()) return false;
            if (getPostalPostCodeValidationMessage()) return false;
        }

        return true;
    };

    const validateInvoiceStatementSection = () => {
        const invoice = formData.invoiceStatementInfo;

        // required
        if (!invoice.contactName?.trim()) return "Contact Name for Invoices is required";

        if (!invoice.email?.trim()) return "Email Address for Invoices is required";
        if (!isValidEmail(invoice.email)) return "Invalid Invoice Email Address";

        if (!invoice.emailForStatements?.trim())
            return "Email Address for Statements is required";
        if (!isValidEmail(invoice.emailForStatements))
            return "Invalid Statement Email Address";

        // optional phone
        if (invoice.phoneNumber?.trim()) {
            if (getInvoicePhoneValidationMessage())
                return getInvoicePhoneValidationMessage();
        }

        // PO validation
        if (invoice.isPoNumberMandatory === "yes") {
            if (!formData.generalInfo.poEmail?.trim())
                return "PO Email is required";

            if (!isValidEmail(formData.generalInfo.poEmail))
                return "Invalid PO Email Address";
        }

        return null;
    };

    const validateCreditAccountSection = () => {
        // Skip validation if credit account not needed
        if (formData.invoiceStatementInfo.needCreditAccount !== "yes") {
            return true;
        }

        const credit = formData.creditAccountInfo;

        if (!credit.contactName?.trim()) return false;

        if (!credit.email?.trim()) return false;
        if (!isValidEmail(credit.email?.trim())) return false;

        if (!credit.position?.trim()) return false;

        if (!credit.phoneNumber?.trim()) return false;

        if (!credit.companyWebsite?.trim()) return false;

        if (!credit.natureOfBusiness?.trim()) return false;

        if (!credit.dateOfIncorporation?.trim()) return false;

        if (!credit.directorName?.trim()) return false;

        if (getCreditAccountContactNameValidationMessage()) return false;

        if (getCreditAccountEmailValidationMessage()) return false;

        if (getCreditAccountPositionValidationMessage()) return false;

        if (getCreditAccountPhoneValidationMessage()) return false;

        if (getCreditAccountCompanyWebsiteValidationMessage()) return false;

        if (getCreditAccountParentCompanyValidationMessage()) return false;

        if (getCreditAccountPreviousBusinessNamesValidationMessage()) return false;

        if (getCreditAccountMaxCreditRequiredValidationMessage()) return false;

        if (getCreditAccountDirectorNameValidationMessage()) return false;

        if (credit.hasCreditReferees === "yes") {
            for (const referee of formData.creditReferees) {
                if (!referee.contactName?.trim()) return false;
                if (!referee.company?.trim()) return false;
                if (!referee.phoneNumber?.trim()) return false;
                if (!referee.email?.trim()) return false;

                // optional: email format check for referees
                if (!isValidEmail(referee.email?.trim())) return false;

                if (getCreditRefereePhoneValidationMessage(referee.phoneNumber)) {
                    return false;
                }
            }
        }

        return true;
    };

    const validateSignatureSection = () => {
        if (getFullNameValidationMessage()) return false;
        if (getCompanyValidationMessage()) return false;
        if (getTitleValidationMessage()) return false;

        return true;
    };

    const handleApproveClick = async () => {
        setShowValidationErrors(true);

        const isValid = await validateForm();

        if (!isValid) return;

        if (formData.elvisClientDetails.code?.trim()) {
            const isMyobCodeValid = await validateMyobCode(
                formData.elvisClientDetails.code.trim()
            );

            if (!isMyobCodeValid) {
                setAlertMessage("Please fix the Client Code error before proceeding");
                setShowSuccessAlert(true);
                return;
            }
        }

        if (!isFormValid()) {
            setAlertMessage("Please fill mandatory ELVIS Client Details and AR Review fields");
            setShowSuccessAlert(true);
            return;
        }

        setShowConfirmDialog(true);
    };

    const handleEditModeToggle = async () => {
        if (isEditMode) {
            const isValid = await validateForm();

            if (!isValid) return;

            setIsEditMode(false);
            handleConfirmApprove(2);
            return;
        }

        setIsEditMode(true);
    };

    const [showReDraftDialog, setShowReDraftDialog] = React.useState(false);
    const [reDraftReason, setReDraftReason] = React.useState('');
    const [reDraftId, setReDraftId] = React.useState(null);

    const handleReDraftClick = (id) => {
        setReDraftId(id);
        setShowReDraftDialog(true);
    };

    const handleReDraftSubmit = () => {
        if (reDraftId) {
            reDraftRejectApi(reDraftId, reDraftReason, 'redraft');
            setShowReDraftDialog(false);
            setReDraftReason('');
            setReDraftId(null);
        }
    };

    const handleConfirmApprove = (status: any) => {
        setShowConfirmDialog(false);
        const normalizedCountry = formData?.elvisClientDetails?.country || 'Australia';
        const resolvedPriceListId = getResolvedPriceListId(formData.elvisClientDetails.priceList);
        let obj: any = {
            "clientOnboardRequest": {
                "proposeClientCode": formData.elvisClientDetails.code,
                "proposedPriceListID": resolvedPriceListId,
                "proposedELECode": formData.elvisClientDetails.ele,
                "proposedReportStateID": normalizedCountry === 'Australia'
                    ? formData.elvisClientDetails.state
                    : formData.elvisClientDetails.state,
                "proposedCountry": normalizedCountry,
                "proposedIsGSTExempt": formData.elvisClientDetails.isGstExempt,
                "proposedBUCode": formData.elvisClientDetails.bu,
                "proposedPaymentTerms": formData.elvisClientDetails.paymentTerms,
                "proposedIsMonthlyInvoice": formData.elvisClientDetails.isInvoiceMonthly,
                "proposedASM": formData.elvisClientDetails.asm,
                "proposedIsPORequired": formData.elvisClientDetails.isPoRequired,
                "proposedIsEurofinsGroup": formData.elvisClientDetails.isEurofinsGroup,
                "id": data.clientOnboardRequest.id,
                "companyName": formData.generalInfo.companyName,
                "tradingName": formData.generalInfo.tradingName,
                "abn": formatABN(formData.generalInfo.abn),
                "eurofinsContactName": formData.generalInfo.eurofinsContactName,
                "business_StreetName": formData.generalInfo.businessStreetNumber,
                "business_Suburb": formData.generalInfo.businessSuburb,
                "business_State": formData.generalInfo.businessState,
                "business_Country": formData.generalInfo.businessCountry,
                "business_PostCode": formData.generalInfo.businessPostCode,
                "isPostalAddressSameAsBusinessAddress": formData.generalInfo.isPostalSameAsBusiness,
                "postal_StreetName": formData.generalInfo.postalStreetNumber,
                "postal_Suburb": formData.generalInfo.postalSuburb,
                "postal_State": formData.generalInfo.postalState,
                "postal_Country": formData.generalInfo.postalCountry,
                "postal_PostCode": formData.generalInfo.postalPostCode,
                "invoicePlatform": formData.invoiceStatementInfo.invoicePlatform,
                "isPONumberMandatory": formData.invoiceStatementInfo.isPoNumberMandatory === 'yes',
                "poEmail": formData.generalInfo.poEmail,
                "hasInvoicePlatform": formData.invoiceStatementInfo.usesInvoicePlatform === 'yes',
                "needCreditAccount": formData.invoiceStatementInfo.needCreditAccount === 'yes',
                "hasAgreedToTermsAndCondition": formData.generalInfo.hasAgreedToTermsAndCondition,
                "tandCComments": formData.generalInfo.tandCComments,
                "title": formData.generalInfo.title,
                "fullName": formData.generalInfo.fullName,
                "company": formData.generalInfo.company,
                "requestedDate": formData.generalInfo.requestedDate,
                "requestStatus": status,
                "abnCheck": formData.elvisClientDetails.abnCheck,
                "refereeCheck": formData.elvisClientDetails.refereeCheck,
                "reviewerComments": formData.elvisClientDetails.reviewerComments || '',
                "signatureId": formData.generalInfo.signatureId,
                "reviewerNotes": formData.generalInfo.reviewerNotes || '',
                "bankLetterFileName": formData.elvisClientDetails.bankLetterFileName || ''
            },
            requestedContacts: (formData.requestedContacts || []).map((contact, index) =>
                index === 0
                    ? {
                        ...contact,
                        name: formData.generalInfo.contactName,
                    }
                    : contact
            ),
            "notifyARTeam": (!isUserVersion && isASM && !isOfficeAdmin)
        }
        if (formData.creditAccountInfo !== null) {
            obj.creditAccountDetail = {
                ...formData.creditAccountInfo,
                isTrustee: formData.creditAccountInfo.isTrustee === 'yes',
                clientOnBoardRequestID: formData.creditAccountInfo.clientOnBoardRequestID,
                id: formData.creditAccountInfo.id || '',
                hasCreditReferees: formData.creditAccountInfo.hasCreditReferees === 'yes'
            }
            obj.creditReferees = formData.creditReferees;
        }

        let selectApi = status === 2 ? reviewOnboardRequest : actionOnboardRequest;

        selectApi(obj).then((res: any) => {
            if (res.status === 200) {
                let successMessage = '';
                switch (status) {
                    case 2:
                        successMessage = 'Successfully Saved';
                        break;
                    case 3:
                        successMessage = 'Successfully Approved';
                        break;
                    case 4:
                        successMessage = 'Request Rejected Successfully';
                        break;
                    default:
                        successMessage = 'Operation completed successfully';
                }

                if (status !== 4) {
                    setShowValidationErrors(false);
                }
                setAlertMessage(successMessage);
                setShowSuccessAlert(true);

                const shouldRedirectToRequestList = status !== 2;

                if (shouldRedirectToRequestList || (!isUserVersion && isASM && !isOfficeAdmin)) {
                    setTimeout(() => {
                        navigate('/home/new-client-request');
                    }, 1500);
                }
                if (status === 2) {
                    handleReview(id, tabValue);
                }
            } else if (res.status === 400) {
                const errorMessage = res.data || 'Bad request - Invalid data provided';
                if (status !== 4) {
                    setShowValidationErrors(true);
                }
                setApiError({
                    status: true,
                    type: 'error',
                    message: errorMessage
                });
            } else {
                if (status !== 4) {
                    setShowValidationErrors(true);
                }
                setAlertMessage(res.data);
                setShowSuccessAlert(true);
            }
        }).catch((error) => {
            const errorMessage = error?.response?.data ||
                error?.response?.data?.error ||
                error?.message ||
                'An error occurred while processing your request';

            if (status !== 4) {
                setShowValidationErrors(true);
            }
            setAlertMessage(errorMessage);
            setShowSuccessAlert(true);
        });
    };

    const confirmReject = () => {
        setShowValidationErrors(false);
        if (formData?.elvisClientDetails?.reviewerComments.trim()) {
            reDraftRejectApi(formData?.generalInfo?.id, formData?.elvisClientDetails?.reviewerComments, 'reject', isDefferred);
        }
        setShowRejectDialog(false);
        handleInputChange('elvisClientDetails', 'reviewerComments', '');
    };

    const paymentTerms = formData?.elvisClientDetails?.paymentTerms;

    const isAbnCheckValid =
        String(paymentTerms) === '0'
            ? true
            : formData?.elvisClientDetails?.abnCheck === true;

    // Bank Letter is optional for COD (paymentTerms === '0'), mandatory otherwise
    const isBankLetterValid =
        String(paymentTerms) === '0'
            ? true
            : !!formData?.elvisClientDetails?.bankLetterFileName;

    const isFormValid = () => {
        const elvisValid =
            formData?.elvisClientDetails?.code?.trim() !== '' &&
            formData?.elvisClientDetails?.priceList !== '' &&
            formData?.elvisClientDetails?.ele !== '' &&
            formData?.elvisClientDetails?.bu !== '' &&
            formData?.elvisClientDetails?.state !== '' &&
            formData?.elvisClientDetails?.paymentTerms !== '' &&
            formData?.elvisClientDetails?.asm !== '' &&
            isBankLetterValid &&
            isAbnCheckValid &&
            myObCodeExists.status === false;

        return elvisValid;
    };

    // Accepts optional reason
    const reDraftRejectApi = (id: any, reason?: string, type?: string, deferred = false) => {
        let obj = {
            id: id,
            reviewComments: reason || '',
            ...(type !== 'redraft' && { isDefferred: deferred })
        }
        let apiCall = type === 'redraft' ? reDraft : sendRejectStatusToCustomer;
        apiCall(obj).then((res: any) => {
            if (res.status === 200) {
                let successMessage = type === 'redraft' ? 'Successfully Re-Drafted' : 'Request Rejected Successfully';
                setAlertMessage(res.data || successMessage);
                setShowValidationErrors(false);
                setShowSuccessAlert(true);
                setTimeout(() => {
                    navigate('/home/new-client-request');
                }, 1500);
            } else {
                let errorMessage = type === 'redraft' ? 'Failed to Re-Draft' : 'Failed to Reject Request';
                setApiError({
                    status: true,
                    type: 'error',
                    message: res.data || errorMessage
                });
            }
        }).catch((error) => {
            const errorMessage = error?.response?.data ||
                error?.response?.data?.error ||
                error?.message ||
                'An error occurred while processing your request';
            setApiError({
                status: true,
                type: 'error',
                message: error?.response?.data || errorMessage
            });
        });
    }


    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleDeleteEmail = (asmToDelete: any) => {
        const recipientToDelete = String(asmToDelete?.bumaEmail || asmToDelete?.email || asmToDelete || '').trim();
        setEmailList(emailList.filter((asm) => String(asm?.bumaEmail || asm?.email || asm || '').trim() !== recipientToDelete));
    };

    const handleSubmitForApproval = () => {
        setContent(defaultText);
        setLastAutoContent(defaultText);
        setShowApprovalDialog(true);
    };

    const validateMyobCode = async (trimmedValue: any) => {
        if (trimmedValue) {
            try {
                const res = await clientCodeExist(trimmedValue);
                if (res?.status === 200 && res?.data === true) {
                    setMyObCodeExists({
                        status: true,
                        message: 'Code already in use!'
                    });
                    return false;
                } else {
                    setMyObCodeExists({
                        status: false,
                        message: ''
                    });
                    return true;
                }
            } catch (error) {
                setMyObCodeExists({
                    status: true,
                    message: 'Error validating client code'
                });
                return false;
            }
        } else {
            setMyObCodeExists({
                status: false,
                message: ''
            });
            return true;
        }
    }

    const permissions: string[] = Array.isArray(labUserCheck?.Permission)
        ? labUserCheck.Permission
        : labUserCheck?.Permission
            ? [labUserCheck.Permission]
            : [];

    const internalHomePath = permissions.includes('StandardQuotes')
        ? '/home/viewQuote'
        : permissions.includes('ManageInternalUsers')
            ? '/home/admin'
            : '';

    const internalAdminPath = permissions.includes('ManageInternalUsers')
        ? '/home/admin'
        : '';

    let pathChange = (labUserCheck && (labUserCheck?.UserType === 'Internal')) ? internalHomePath : '/home';

    // Print functionality
    const handlePrint = () => {
        const printContainer = document.querySelector('.print-container') as HTMLElement;
        if (!printContainer) return;

        const cmToPx = (cm: number) => (96 / 2.54) * cm;
        const pageHeightPx = cmToPx(29.7);
        const footerHeightPx = cmToPx(1.8);

        // Exclude MUI emotion styles — printStyles inside printContainer handles all styling
        const allStyles = Array.from(document.querySelectorAll('style:not([data-emotion]), link[rel="stylesheet"]'))
            .map(el => el.outerHTML)
            .join('\n');

        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
        document.body.appendChild(iframe);

        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc && iframe.contentWindow) {
            const printClone = printContainer.cloneNode(true) as HTMLElement;
            printClone.style.display = 'block';
            const desiredPrintTitle = 'New Client Request Details';

            iframeDoc.open();
            iframeDoc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${desiredPrintTitle}</title><base href="${window.location.origin}/">${allStyles}</head><body style="margin:0;padding:0">${printClone.outerHTML}</body></html>`);
            iframeDoc.close();
            iframeDoc.title = desiredPrintTitle;
            iframe.contentWindow.focus();

            setTimeout(() => {
                const clonedContainer = iframeDoc.querySelector('.print-container') as HTMLElement | null;
                const footerLayer = iframeDoc.querySelector('.print-footer-layer') as HTMLElement | null;

                if (clonedContainer && footerLayer) {
                    const totalHeight = clonedContainer.scrollHeight;
                    const pageCount = Math.max(1, Math.ceil(totalHeight / pageHeightPx));

                    footerLayer.innerHTML = Array.from({ length: pageCount }, (_, index) => {
                        const footerTop = ((index + 1) * pageHeightPx) - footerHeightPx;
                        return `
                            <div class="print-generated-footer" style="position:absolute;left:0;right:0;top:${footerTop}px;height:${footerHeightPx}px;">
                                <span class="print-generated-footer-date">${new Date().toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                                <span class="print-generated-footer-company">Eurofins Environment Testing Pty Ltd ABN50 005 085 521</span>
                                <span class="print-generated-footer-page">Page ${index + 1}</span>
                            </div>`;
                    }).join('');
                }

                const originalTitle = document.title;
                document.title = desiredPrintTitle;
                iframe.contentWindow?.print();
                setTimeout(() => {
                    document.title = originalTitle;
                    document.body.removeChild(iframe);
                }, 1000);
            }, 300);
        }
    };

    let nav: any = [
        { name: 'Home', path: pathChange },
        ...(permissions.includes('ReviewOnboardRequest') ? [{ name: 'Admin', path: internalAdminPath }] : []),
        { name: 'New Client Request', path: '/home/new-client-request' },
        { name: 'Review Onboard Client Form', path: '/home/review-client-request' }
    ];

    const toTitleCase = (str = '') =>
        str
            .toLowerCase()
            .trim()
            .split(/\s+/)
            .filter(Boolean)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

    const elvis = formData?.elvisClientDetails;

    const isElvisFormValid =
        !!elvis?.priceList &&
        !!elvis?.asm &&
        !!elvis?.ele &&
        !!elvis?.bu &&
        !!elvis?.state;

    const canEditElvis =
        !isUserVersion && isASM && !isOfficeAdmin;

    const isCreditRequired = showValidationErrors && formData.invoiceStatementInfo.needCreditAccount === 'yes';

    const isEmpty = (val) => !val || String(val).trim() === '';

    const isRequiredError = (value) => {
        return showValidationErrors && (!value || String(value).trim() === '');
    };

    const abnValue = formData.generalInfo.abn?.replace(/\D/g, '');

    const isPaymentTermsZero =
        String(formData?.elvisClientDetails?.paymentTerms) === "0";

    const hasAbnValue = abnValue && abnValue.length > 0;

    const abnError =
        showValidationErrors &&
        (
            (!isPaymentTermsZero && !hasAbnValue) || // required only when paymentTerms is not 0
            (hasAbnValue && abnValue.length !== 11)  // validate whenever user enters value
        );

    const abnHelperText =
        showValidationErrors &&
            !isPaymentTermsZero &&
            !hasAbnValue
            ? "ABN is required"
            : showValidationErrors &&
                hasAbnValue &&
                abnValue.length !== 11
                ? "ABN must be exactly 11 digits"
                : "";

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', cursor: 'pointer' }}>
                <Breadcrumb navList={nav} />
            </div>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom align='left' mb={0}>
                    Review Onboard Client Form
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* Print Button */}
                    {labUserCheck?.UserType === 'Internal' && <Button
                        variant="outlined"
                        size="medium"
                        onClick={handlePrint}
                        startIcon={<PrintIcon />}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            minWidth: '120px',
                            borderColor: '#1976d2',
                            color: '#1976d2',
                            '&:hover': {
                                backgroundColor: '#e3f2fd',
                                borderColor: '#1565c0',
                                boxShadow: 2
                            }
                        }}
                    >
                        Print
                    </Button>}

                    {/* Edit Button */}
                    {!isDisabled && isOfficeAdmin && (
                        <Button
                            variant={isEditMode ? "contained" : "outlined"}
                            size="medium"
                            onClick={handleEditModeToggle}
                            startIcon={isEditMode ? <SaveIcon /> : <EditIcon />}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                minWidth: '120px',
                                boxShadow: isEditMode ? 3 : 0,
                                '&:hover': {
                                    boxShadow: isEditMode ? 5 : 2
                                }
                            }}
                        >
                            {isEditMode ? 'Save Changes' : 'Edit Form'}
                        </Button>
                    )}
                </Box>
            </Box>
            <Box sx={{ p: 0 }}>
                <Grid container spacing={3}>
                    {/* Left Side - Combined General & Invoice Information */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: 'fit-content', minHeight: '700px', boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.2),1px 0px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)' }}>
                            <CardContent sx={{ p: 3 }}>
                                {/* General Information Section */}
                                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, borderBottom: '2px solid', pb: 1 }}>
                                    General Information
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: 4 }}>
                                    <Grid item xs={6}>
                                        <Input
                                            label="Name / Company Name / Business Name"
                                            value={formData.generalInfo.companyName}
                                            onChange={(e) => handleInputChange('generalInfo', 'companyName', e.target.value)}
                                            fullWidth
                                            required
                                            size="small"
                                            disabled={isFieldsDisabled}
                                            error={
                                                isRequiredError(formData.generalInfo.companyName) ||
                                                !!getCompanyNameValidationMessage()
                                            }
                                            helperText={
                                                isRequiredError(formData.generalInfo.companyName)
                                                    ? 'Company Name is required'
                                                    : getCompanyNameValidationMessage()
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Input
                                            label="ABN"
                                            value={formatABN(formData.generalInfo.abn) || ''}
                                            onChange={(e) => {
                                                const value = e.target.value;

                                                const digits = value.replace(/\D/g, '').slice(0, 11);

                                                const formatted = digits
                                                    .replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, '$1 $2 $3 $4')
                                                    .trim();

                                                handleInputChange('generalInfo', 'abn', formatted);
                                            }}
                                            fullWidth
                                            size="small"
                                            inputProps={{
                                                inputMode: 'numeric',
                                                maxLength: 14 // 11 digits + 3 spaces
                                            }}
                                            disabled={isFieldsDisabled}
                                            required
                                            error={abnError}
                                            helperText={abnHelperText}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Input
                                            label="Trading Name"
                                            value={formData.generalInfo.tradingName}
                                            onChange={(e) => handleInputChange('generalInfo', 'tradingName', e.target.value)}
                                            fullWidth
                                            size="small"
                                            required
                                            disabled={isFieldsDisabled}
                                            error={
                                                isRequiredError(formData.generalInfo.tradingName) ||
                                                !!getTradingNameValidationMessage()
                                            }
                                            helperText={
                                                isRequiredError(formData.generalInfo.tradingName)
                                                    ? 'Trading Name is required'
                                                    : getTradingNameValidationMessage()
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Input
                                            label="Name of Eurofins Contact"
                                            value={formData.generalInfo.eurofinsContactName}
                                            onChange={(e) => handleInputChange('generalInfo', 'eurofinsContactName', e.target.value)}
                                            onBlur={(e) => handleInputChange('generalInfo', 'eurofinsContactName', toTitleCase(e.target.value))}
                                            fullWidth
                                            required
                                            size="small"
                                            disabled={isFieldsDisabled}
                                            error={!!getEurofinsContactNameValidationMessage()}
                                            helperText={getEurofinsContactNameValidationMessage()}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Input
                                            label="Contact Name(Full Name)"
                                            value={formData.generalInfo.contactName}
                                            onChange={(e) => handleInputChange('generalInfo', 'contactName', e.target.value)}
                                            onBlur={(e) => handleInputChange('generalInfo', 'contactName', toTitleCase(e.target.value))}
                                            fullWidth
                                            required
                                            size="small"
                                            disabled={isFieldsDisabled}
                                            error={
                                                isRequiredError(formData.generalInfo.contactName) ||
                                                !!getContactNameValidationMessage()
                                            }
                                            helperText={
                                                isRequiredError(formData.generalInfo.contactName)
                                                    ? 'Contact Name is required'
                                                    : getContactNameValidationMessage()
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Input
                                            label="Email Address"
                                            value={formData.generalInfo.emailAddress}
                                            onChange={(e) => handleInputChange('generalInfo', 'emailAddress', e.target.value)}
                                            fullWidth
                                            type="email"
                                            size="small"
                                            required
                                            disabled={true}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel
                                                id="general-contact-number-type-label"
                                                sx={{ fontSize: '12px' }}
                                            >
                                                Contact Number Type
                                            </InputLabel>

                                            <Select
                                                labelId="general-contact-number-type-label"
                                                value={generalContactNumberType}
                                                label="Contact Number Type"
                                                required
                                                onChange={(e) =>
                                                    handleGeneralContactNumberTypeChange(
                                                        e.target.value as ContactNumberType
                                                    )
                                                }
                                                disabled={isFieldsDisabled}
                                                sx={{ fontSize: '12px' }}
                                            >
                                                <MenuItem value="phoneNumber" sx={{ fontSize: '12px' }}>
                                                    Phone Number
                                                </MenuItem>

                                                <MenuItem value="mobileNumber" sx={{ fontSize: '12px' }}>
                                                    Mobile Number
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        {generalContactNumberType === 'phoneNumber' && (
                                            <Input
                                                label="Phone Number"
                                                value={getGeneralContactNumberValue()}
                                                onChange={(e) => {
                                                    const value = e.target.value;

                                                    if (value === '' || /^[0-9]+$/.test(value)) {
                                                        handleGeneralContactNumberChange(value);
                                                    }
                                                }}
                                                fullWidth
                                                required
                                                size="small"
                                                error={!!getGeneralContactNumberValidationMessage()}
                                                helperText={getGeneralContactNumberValidationMessage()}
                                                disabled={isFieldsDisabled}
                                            />
                                        )}

                                        {generalContactNumberType === 'mobileNumber' && (
                                            <Input
                                                label="Mobile Number"
                                                value={getGeneralContactNumberValue()}
                                                onChange={(e) => {
                                                    const value = e.target.value;

                                                    if (value === '' || /^[0-9]+$/.test(value)) {
                                                        handleGeneralContactNumberChange(value);
                                                    }
                                                }}
                                                fullWidth
                                                required
                                                size="small"
                                                error={!!getGeneralContactNumberValidationMessage()}
                                                helperText={getGeneralContactNumberValidationMessage()}
                                                disabled={isFieldsDisabled}
                                            />
                                        )}
                                    </Grid>

                                    {/* Business Address Section */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, mt: 1 }}>
                                            Business Address
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Input
                                            label="Number / Street Name"
                                            value={formData.generalInfo.businessStreetNumber}
                                            onChange={(e) => handleInputChange('generalInfo', 'businessStreetNumber', e.target.value)}
                                            onBlur={(e) => handleInputChange('generalInfo', 'businessStreetNumber', toTitleCase(e.target.value))}
                                            fullWidth
                                            size="small"
                                            required
                                            disabled={isFieldsDisabled}
                                            error={
                                                isRequiredError(formData.generalInfo.businessStreetNumber) ||
                                                !!getBusinessStreetValidationMessage()
                                            }
                                            helperText={
                                                isRequiredError(formData.generalInfo.businessStreetNumber)
                                                    ? 'Street is required'
                                                    : getBusinessStreetValidationMessage()
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Input
                                            label="Suburb"
                                            value={formData.generalInfo.businessSuburb}
                                            onChange={(e) => handleInputChange('generalInfo', 'businessSuburb', e.target.value)}
                                            onBlur={(e) => handleInputChange('generalInfo', 'businessSuburb', toTitleCase(e.target.value))}
                                            fullWidth
                                            size="small"
                                            error={
                                                isRequiredError(formData.generalInfo.businessSuburb) ||
                                                !!getBusinessSuburbValidationMessage()
                                            }
                                            helperText={
                                                isRequiredError(formData.generalInfo.businessSuburb)
                                                    ? 'Suburb is required'
                                                    : getBusinessSuburbValidationMessage()
                                            }
                                            required
                                            disabled={isFieldsDisabled}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Input
                                            label="Post Code"
                                            value={formData.generalInfo.businessPostCode}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/[^0-9]/g, '');
                                                handleInputChange('generalInfo', 'businessPostCode', value);
                                            }}
                                            fullWidth
                                            size="small"
                                            error={
                                                isRequiredError(formData.generalInfo.businessPostCode) ||
                                                !!getBusinessPostCodeValidationMessage()
                                            }
                                            helperText={
                                                isRequiredError(formData.generalInfo.businessPostCode)
                                                    ? 'Post Code is required'
                                                    : getBusinessPostCodeValidationMessage()
                                            }
                                            required
                                            disabled={isFieldsDisabled}
                                            inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel id="business-country-label" sx={{ fontSize: '12px' }}>Country</InputLabel>
                                            <Select
                                                labelId="business-country-label"
                                                value={formData.generalInfo.businessCountry}
                                                label="Country"
                                                onChange={(e) => handleAddressSelectChange('business', 'Country', e.target.value)}
                                                disabled={isFieldsDisabled}
                                                sx={{ fontSize: '12px' }}
                                            >
                                                {countryOptions.map((option) => (
                                                    <MenuItem key={option} value={option} sx={{ fontSize: '12px' }}>{option}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        {getStateOptions(formData.generalInfo.businessCountry).length > 0 ? (
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="business-state-label" sx={{ fontSize: '12px' }}>State</InputLabel>
                                                <Select
                                                    labelId="business-state-label"
                                                    value={formData.generalInfo.businessState}
                                                    label="State"
                                                    onChange={(e) => {
                                                        handleAddressSelectChange('business', 'State', e.target.value);
                                                    }}
                                                    disabled={isFieldsDisabled}
                                                    sx={{ fontSize: '12px' }}
                                                >
                                                    {getStateOptions(formData.generalInfo.businessCountry).map((option) => (
                                                        <MenuItem key={option.id} value={option.id} sx={{ fontSize: '12px' }}>{option.name}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        ) : (
                                            <Input
                                                label="State"
                                                value={formData.generalInfo.businessState}
                                                onChange={(e) => {
                                                    handleInputChange('generalInfo', 'businessState', e.target.value);
                                                }}
                                                fullWidth
                                                size="small"
                                                required
                                                disabled={isFieldsDisabled}
                                            />
                                        )}
                                    </Grid>

                                    {/* Postal Address Checkbox */}
                                    <Grid item xs={6} sx={{ mt: 2 }}>
                                        <Typography variant="body2" sx={{
                                            fontWeight: 500,
                                            display: 'flex',
                                            alignItems: 'center',
                                            height: '40px'
                                        }}>
                                            Is Billing address same as business address? <Typography component="span" sx={{ color: 'red' }}>*</Typography>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sx={{ mt: 2 }}>
                                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', height: '40px' }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={formData.generalInfo.isPostalSameAsBusiness === true}
                                                        onChange={() => handlePostalAddressChange(true)}
                                                        color="primary"
                                                        size="small"
                                                        disabled={isFieldsDisabled}
                                                    />
                                                }
                                                label="Yes"
                                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                                                disabled={isFieldsDisabled}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={formData.generalInfo.isPostalSameAsBusiness === false}
                                                        onChange={() => handlePostalAddressChange(false)}
                                                        color="primary"
                                                        size="small"
                                                        disabled={isFieldsDisabled}
                                                    />
                                                }
                                                label="No"
                                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                                                disabled={isFieldsDisabled}
                                            />
                                        </Box>
                                    </Grid>

                                    {/* Billing Address Section - Only show if checkbox is unchecked */}
                                    {!formData.generalInfo.isPostalSameAsBusiness && (
                                        <>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'secondary.main' }}>
                                                    Billing Address
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Input
                                                    label="Number / Street Name"
                                                    value={formData.generalInfo.postalStreetNumber}
                                                    onChange={(e) => handleInputChange('generalInfo', 'postalStreetNumber', e.target.value)}
                                                    onBlur={(e) => handleInputChange('generalInfo', 'postalStreetNumber', toTitleCase(e.target.value))}
                                                    fullWidth
                                                    size="small"
                                                    error={
                                                        isRequiredError(formData.generalInfo.postalStreetNumber) ||
                                                        !!getPostalStreetValidationMessage()
                                                    }
                                                    helperText={
                                                        isRequiredError(formData.generalInfo.postalStreetNumber)
                                                            ? 'Street is required'
                                                            : getPostalStreetValidationMessage()
                                                    }
                                                    required
                                                    disabled={isFieldsDisabled}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Input
                                                    label="Suburb"
                                                    value={formData.generalInfo.postalSuburb}
                                                    onChange={(e) => handleInputChange('generalInfo', 'postalSuburb', e.target.value)}
                                                    onBlur={(e) => handleInputChange('generalInfo', 'postalSuburb', toTitleCase(e.target.value))}
                                                    fullWidth
                                                    size="small"
                                                    error={
                                                        isRequiredError(formData.generalInfo.postalSuburb) ||
                                                        !!getPostalSuburbValidationMessage()
                                                    }
                                                    helperText={
                                                        isRequiredError(formData.generalInfo.postalSuburb)
                                                            ? 'Suburb is required'
                                                            : getPostalSuburbValidationMessage()
                                                    }
                                                    required
                                                    disabled={isFieldsDisabled}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Input
                                                    label="Post Code"
                                                    value={formData.generalInfo.postalPostCode}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/[^0-9]/g, '');
                                                        handleInputChange('generalInfo', 'postalPostCode', value);
                                                    }}
                                                    fullWidth
                                                    size="small"
                                                    error={
                                                        isRequiredError(formData.generalInfo.postalPostCode) ||
                                                        !!getPostalPostCodeValidationMessage()
                                                    }
                                                    helperText={
                                                        isRequiredError(formData.generalInfo.postalPostCode)
                                                            ? 'Post Code is required'
                                                            : getPostalPostCodeValidationMessage()
                                                    }
                                                    required
                                                    disabled={isFieldsDisabled}
                                                    inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel id="postal-country-label">Country</InputLabel>
                                                    <Select
                                                        labelId="postal-country-label"
                                                        value={formData.generalInfo.postalCountry}
                                                        label="Country"
                                                        onChange={(e) => handleAddressSelectChange('postal', 'Country', e.target.value)}
                                                        disabled={isFieldsDisabled}
                                                    >
                                                        {countryOptions.map((option) => (
                                                            <MenuItem key={option} value={option}>{option}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={6}>
                                                {getStateOptions(formData.generalInfo.postalCountry).length > 0 ? (
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel id="postal-state-label">State</InputLabel>
                                                        <Select
                                                            labelId="postal-state-label"
                                                            value={formData.generalInfo.postalState}
                                                            label="State"
                                                            onChange={(e) => handleAddressSelectChange('postal', 'State', e.target.value)}
                                                            disabled={isFieldsDisabled}
                                                        >
                                                            {getStateOptions(formData.generalInfo.postalCountry).map((option) => (
                                                                <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                ) : (
                                                    <Input
                                                        label="State"
                                                        value={formData.generalInfo.postalState}
                                                        onChange={(e) => handleInputChange('generalInfo', 'postalState', e.target.value)}
                                                        fullWidth
                                                        size="small"
                                                        required
                                                        disabled={isFieldsDisabled}
                                                    />
                                                )}
                                            </Grid>
                                        </>
                                    )}
                                </Grid>

                                <Divider sx={{ my: 3 }} />

                                {/* Invoice/Statement Information Section */}
                                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, borderBottom: '2px solid', pb: 1 }}>
                                    Invoice / Statement
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Input
                                            label="Contact Name for Invoices"
                                            value={formData.invoiceStatementInfo.contactName}
                                            onChange={(e) =>
                                                handleInputChange('invoiceStatementInfo', 'contactName', e.target.value)
                                            }
                                            onBlur={(e) =>
                                                handleInputChange('invoiceStatementInfo', 'contactName', toTitleCase(e.target.value))
                                            }
                                            fullWidth
                                            size="small"
                                            required
                                            disabled={isFieldsDisabled}
                                            error={
                                                showValidationErrors &&
                                                (
                                                    !formData.invoiceStatementInfo.contactName?.trim() ||
                                                    !!getContactNameForInvoicesValidationMessage()
                                                )
                                            }
                                            helperText={
                                                showValidationErrors
                                                    ? getContactNameForInvoicesValidationMessage()
                                                    : ""
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Input
                                            label="Phone Number"
                                            value={formData.invoiceStatementInfo.phoneNumber}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === '' || /^[0-9]+$/.test(value)) {
                                                    handleInputChange('invoiceStatementInfo', 'phoneNumber', value);
                                                }
                                            }}
                                            fullWidth
                                            size="small"
                                            error={
                                                !!formData.invoiceStatementInfo.phoneNumber?.trim() &&
                                                !!getInvoicePhoneValidationMessage()
                                            }
                                            helperText={
                                                formData.invoiceStatementInfo.phoneNumber?.trim()
                                                    ? getInvoicePhoneValidationMessage()
                                                    : ""
                                            }
                                            disabled={isFieldsDisabled}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Input
                                            label="Email Address for Invoices"
                                            value={formData.invoiceStatementInfo.email}
                                            onChange={(e) =>
                                                handleInputChange('invoiceStatementInfo', 'email', e.target.value)
                                            }
                                            fullWidth
                                            size="small"
                                            error={
                                                showValidationErrors &&
                                                (!formData.invoiceStatementInfo.email?.trim() ||
                                                    !isValidEmail(formData.invoiceStatementInfo.email))
                                            }
                                            helperText={
                                                !formData.invoiceStatementInfo.email?.trim()
                                                    ? "Email is required"
                                                    : !isValidEmail(formData.invoiceStatementInfo.email)
                                                        ? "Invalid email format"
                                                        : ""
                                            }
                                            required
                                            disabled={isFieldsDisabled}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Input
                                            label="Email Address for Statements"
                                            value={formData.invoiceStatementInfo.emailForStatements}
                                            onChange={(e) =>
                                                handleInputChange('invoiceStatementInfo', 'emailForStatements', e.target.value)
                                            }
                                            fullWidth
                                            size="small"
                                            error={
                                                showValidationErrors &&
                                                (!formData.invoiceStatementInfo.emailForStatements?.trim() ||
                                                    !isValidEmail(formData.invoiceStatementInfo.emailForStatements))
                                            }
                                            helperText={
                                                !formData.invoiceStatementInfo.emailForStatements?.trim()
                                                    ? "Email is required"
                                                    : !isValidEmail(formData.invoiceStatementInfo.emailForStatements)
                                                        ? "Invalid email format"
                                                        : ""
                                            }
                                            required
                                            disabled={isFieldsDisabled}
                                        />
                                    </Grid>

                                    {/* New Required Questions with Radio Buttons */}
                                    <Grid item xs={6}>
                                        <Typography variant="body2" sx={{
                                            fontWeight: 500,
                                            display: 'flex',
                                            alignItems: 'center',
                                            height: '40px'
                                        }}>
                                            Is PO number mandatory for invoicing? <Typography component="span" sx={{ color: 'red' }}>*</Typography>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <RadioGroup
                                            row
                                            value={formData.invoiceStatementInfo.isPoNumberMandatory}
                                            onChange={e => handleRadioChange('isPoNumberMandatory', e.target.value)}
                                        >
                                            <FormControlLabel value="yes" control={<Radio size="small" color="primary" disabled={isFieldsDisabled} />} label="Yes" sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }} disabled={isFieldsDisabled} />
                                            <FormControlLabel value="no" control={<Radio size="small" color="primary" disabled={isFieldsDisabled} />} label="No" sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }} disabled={isFieldsDisabled} />
                                        </RadioGroup>
                                    </Grid>
                                    {formData.invoiceStatementInfo.isPoNumberMandatory === 'yes' && (
                                        <Grid item xs={12}>
                                            <Input
                                                label="Email address for PO Number"
                                                value={formData.generalInfo.poEmail || ''}
                                                onChange={(e) =>
                                                    handleInputChange('generalInfo', 'poEmail', e.target.value)
                                                }
                                                fullWidth
                                                size="small"
                                                disabled={isFieldsDisabled}
                                                error={
                                                    showValidationErrors &&
                                                    (
                                                        !formData.generalInfo.poEmail?.trim() ||
                                                        !!getPoEmailValidationMessage()
                                                    )
                                                }
                                                helperText={
                                                    showValidationErrors
                                                        ? getPoEmailValidationMessage()
                                                        : ""
                                                }
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={6}>
                                        <Typography variant="body2" sx={{
                                            fontWeight: 500,
                                            display: 'flex',
                                            alignItems: 'center',
                                            height: '40px'
                                        }}>
                                            Does your company/corporation use an invoice platform for submitting & delivery of invoices?
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <RadioGroup
                                            row
                                            value={formData.invoiceStatementInfo.usesInvoicePlatform}
                                            onChange={e => handleRadioChange('usesInvoicePlatform', e.target.value)}
                                        >
                                            <FormControlLabel value="yes" control={<Radio size="small" color="primary" disabled={isFieldsDisabled} />} label="Yes" sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }} disabled={isFieldsDisabled} />
                                            <FormControlLabel value="no" control={<Radio size="small" color="primary" disabled={isFieldsDisabled} />} label="No" sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }} disabled={isFieldsDisabled} />
                                        </RadioGroup>
                                    </Grid>
                                    {formData.invoiceStatementInfo.usesInvoicePlatform === 'yes' && (
                                        <Grid item xs={12}>
                                            <Input
                                                label="Name of Invoice Platform"
                                                value={formData.invoiceStatementInfo.invoicePlatform}
                                                onChange={(e) =>
                                                    handleInputChange('invoiceStatementInfo', 'invoicePlatform', e.target.value)
                                                }
                                                onBlur={(e) =>
                                                    handleInputChange('invoiceStatementInfo', 'invoicePlatform', toTitleCase(e.target.value))
                                                }
                                                fullWidth
                                                size="small"
                                                disabled={isFieldsDisabled}
                                                error={
                                                    showValidationErrors &&
                                                    !formData.invoiceStatementInfo.invoicePlatform?.trim()
                                                }
                                                helperText={
                                                    showValidationErrors &&
                                                        !formData.invoiceStatementInfo.invoicePlatform?.trim()
                                                        ? "Invoice Platform is required"
                                                        : ""
                                                }
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={6}>
                                        <Typography variant="body2" sx={{
                                            fontWeight: 500,
                                            display: 'flex',
                                            alignItems: 'center',
                                            height: '40px'
                                        }}>
                                            Do you wish to apply for a 30-day credit account? <Typography component="span" sx={{ color: 'red' }}>*</Typography>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <RadioGroup
                                            row
                                            value={formData.invoiceStatementInfo.needCreditAccount}
                                            onChange={e => handleRadioChange('needCreditAccount', e.target.value)}
                                        >
                                            <FormControlLabel value="yes" control={<Radio size="small" color="primary" disabled={isFieldsDisabled} />} label="Yes" sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }} disabled={isFieldsDisabled} />
                                            <FormControlLabel value="no" control={<Radio size="small" color="primary" disabled={isFieldsDisabled} />} label="No" sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }} disabled={isFieldsDisabled} />
                                        </RadioGroup>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Right Side - Combined Credit Account & Referees */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: 'fit-content', minHeight: '700px', boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.2),1px 0px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)' }}>
                            <CardContent sx={{ p: 3 }}>
                                {/* Credit Account Details Section */}
                                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, borderBottom: '2px solid', pb: 1 }}>
                                    Credit Account Details
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: 4 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Input
                                            label="Contact Name"
                                            value={formData?.creditAccountInfo?.contactName}
                                            onChange={(e) =>
                                                handleInputChange('creditAccountInfo', 'contactName', e.target.value)
                                            }
                                            onBlur={(e) =>
                                                handleInputChange('creditAccountInfo', 'contactName', toTitleCase(e.target.value))
                                            }
                                            fullWidth
                                            size="small"
                                            required
                                            disabled={isCreditAccountDisabled}
                                            error={
                                                (isCreditRequired && isEmpty(formData?.creditAccountInfo?.contactName)) ||
                                                !!getCreditAccountContactNameValidationMessage()
                                            }
                                            helperText={
                                                isCreditRequired && isEmpty(formData?.creditAccountInfo?.contactName)
                                                    ? 'Contact Name is required'
                                                    : getCreditAccountContactNameValidationMessage()
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Input
                                            label="Email Address"
                                            value={formData?.creditAccountInfo?.email}
                                            onChange={(e) =>
                                                handleInputChange("creditAccountInfo", "email", e.target.value)
                                            }
                                            fullWidth
                                            type="email"
                                            size="small"
                                            required
                                            disabled={isCreditAccountDisabled}
                                            error={
                                                (isCreditRequired &&
                                                    isEmpty(formData?.creditAccountInfo?.email)) ||
                                                !!getCreditAccountEmailValidationMessage()
                                            }
                                            helperText={
                                                isCreditRequired &&
                                                    isEmpty(formData?.creditAccountInfo?.email)
                                                    ? "Email Address is required"
                                                    : getCreditAccountEmailValidationMessage()
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Input
                                            label="Position"
                                            value={formData?.creditAccountInfo?.position}
                                            onChange={(e) =>
                                                handleInputChange('creditAccountInfo', 'position', e.target.value)
                                            }
                                            onBlur={(e) =>
                                                handleInputChange('creditAccountInfo', 'position', toTitleCase(e.target.value))
                                            }
                                            fullWidth
                                            size="small"
                                            required
                                            disabled={isCreditAccountDisabled}
                                            error={
                                                (isCreditRequired && isEmpty(formData?.creditAccountInfo?.position)) ||
                                                !!getCreditAccountPositionValidationMessage()
                                            }
                                            helperText={
                                                isCreditRequired && isEmpty(formData?.creditAccountInfo?.position)
                                                    ? 'Position is required'
                                                    : getCreditAccountPositionValidationMessage()
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Input
                                            label="Phone Number"
                                            value={formData?.creditAccountInfo?.phoneNumber}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === '' || /^[0-9]+$/.test(value)) {
                                                    handleInputChange('creditAccountInfo', 'phoneNumber', value);
                                                }
                                            }}
                                            fullWidth
                                            size="small"
                                            required
                                            disabled={isCreditAccountDisabled}
                                            error={
                                                (isCreditRequired && isEmpty(formData?.creditAccountInfo?.phoneNumber)) ||
                                                !!getCreditAccountPhoneValidationMessage()
                                            }
                                            helperText={
                                                isCreditRequired && isEmpty(formData?.creditAccountInfo?.phoneNumber)
                                                    ? 'Phone Number is required'
                                                    : getCreditAccountPhoneValidationMessage()
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Input
                                            label="Company Website"
                                            value={formData?.creditAccountInfo?.companyWebsite}
                                            onChange={(e) =>
                                                handleInputChange('creditAccountInfo', 'companyWebsite', e.target.value)
                                            }
                                            fullWidth
                                            size="small"
                                            required
                                            disabled={isCreditAccountDisabled}
                                            error={
                                                (isCreditRequired && isEmpty(formData?.creditAccountInfo?.companyWebsite)) ||
                                                !!getCreditAccountCompanyWebsiteValidationMessage()
                                            }
                                            helperText={
                                                isCreditRequired && isEmpty(formData?.creditAccountInfo?.companyWebsite)
                                                    ? 'Company Website is required'
                                                    : getCreditAccountCompanyWebsiteValidationMessage()
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth size="small" required error={
                                            isCreditRequired &&
                                            isEmpty(formData?.creditAccountInfo?.natureOfBusiness)
                                        }>
                                            <InputLabel sx={{ fontSize: '12px' }} disabled={isCreditAccountDisabled}>Nature of Business</InputLabel>
                                            <Select
                                                value={formData?.creditAccountInfo?.natureOfBusiness}
                                                disabled={isCreditAccountDisabled}
                                                label="Nature of Business"
                                                onChange={(e) => handleInputChange('creditAccountInfo', 'natureOfBusiness', e.target.value)}
                                                data-testid="nature-of-business-select"
                                                sx={{ fontSize: '12px' }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: {
                                                            maxHeight: 200,
                                                            overflowY: 'auto'
                                                        }
                                                    },
                                                    anchorOrigin: {
                                                        vertical: 'bottom',
                                                        horizontal: 'left'
                                                    },
                                                    transformOrigin: {
                                                        vertical: 'top',
                                                        horizontal: 'left'
                                                    }
                                                }}
                                            >
                                                <MenuItem value="Consultant – Environmental/Engineering">Consultant – Environmental/Engineering</MenuItem>
                                                <MenuItem value="Consultant – OHS">Consultant – OHS</MenuItem>
                                                <MenuItem value="Consultant – Other">Consultant – Other</MenuItem>
                                                <MenuItem value="Government/Council">Government/Council</MenuItem>
                                                <MenuItem value="Industry – Trade Waste">Industry – Trade Waste</MenuItem>
                                                <MenuItem value="Industry – Water Board">Industry – Water Board</MenuItem>
                                                <MenuItem value="Industry – Other">Industry – Other</MenuItem>
                                                <MenuItem value="Private">Private</MenuItem>
                                                <MenuItem value="Other">Other</MenuItem>
                                            </Select>
                                            {isCreditRequired && isEmpty(formData?.creditAccountInfo?.natureOfBusiness) && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                                    Nature of Business is required
                                                </Typography>
                                            )}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Input
                                            label="Date of Incorporation"
                                            type="date"
                                            value={formData?.creditAccountInfo?.dateOfIncorporation}
                                            onChange={(e) =>
                                                handleInputChange('creditAccountInfo', 'dateOfIncorporation', e.target.value)
                                            }
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ max: new Date().toISOString().split('T')[0] }}
                                            required
                                            disabled={isCreditAccountDisabled}
                                            error={
                                                isCreditRequired &&
                                                isEmpty(formData?.creditAccountInfo?.dateOfIncorporation)
                                            }
                                            helperText={
                                                isCreditRequired &&
                                                    isEmpty(formData?.creditAccountInfo?.dateOfIncorporation)
                                                    ? 'Date of Incorporation is required'
                                                    : ''
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Input
                                            label="Parent Company (if any)"
                                            value={formData?.creditAccountInfo?.parentCompany}
                                            onChange={(e) => handleInputChange('creditAccountInfo', 'parentCompany', e.target.value)}
                                            onBlur={(e) => handleInputChange('creditAccountInfo', 'parentCompany', toTitleCase(e.target.value))}
                                            fullWidth
                                            size="small"
                                            disabled={isCreditAccountDisabled}
                                            error={!!getCreditAccountParentCompanyValidationMessage()}
                                            helperText={getCreditAccountParentCompanyValidationMessage()}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12}>
                                        <Input
                                            label="Previous Business Name/s (if any)"
                                            value={formData?.creditAccountInfo?.previousBusinessNames}
                                            onChange={(e) => handleInputChange('creditAccountInfo', 'previousBusinessNames', e.target.value)}
                                            onBlur={(e) => handleInputChange('creditAccountInfo', 'previousBusinessNames', toTitleCase(e.target.value))}
                                            fullWidth
                                            size="small"
                                            multiline
                                            rows={2}
                                            disabled={isCreditAccountDisabled}
                                            error={!!getCreditAccountPreviousBusinessNamesValidationMessage()}
                                            helperText={getCreditAccountPreviousBusinessNamesValidationMessage()}
                                        />
                                    </Grid>
                                </Grid>

                                {/* Maximum Credit Required Section */}
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={6} md={6}>
                                        <Input
                                            value={formData?.creditAccountInfo?.maxCreditRequired}
                                            onChange={(e) => handleInputChange('creditAccountInfo', 'maxCreditRequired', e.target.value)}
                                            fullWidth
                                            size="small"
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                            }}
                                            label='Maximum Credit Required'
                                            disabled={isCreditAccountDisabled}
                                            error={!!getCreditAccountMaxCreditRequiredValidationMessage()}
                                            helperText={getCreditAccountMaxCreditRequiredValidationMessage()}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={6}>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            Note: Amounts payable within 30 days from the invoice date
                                        </Typography>
                                    </Grid>
                                </Grid>

                                {/* Trustee Question Section */}
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" sx={{
                                            fontWeight: 500,
                                            display: 'flex',
                                            alignItems: 'center',
                                            height: '40px',
                                            color: 'text.primary'
                                        }}>
                                            Is the applicant a trustee for any trust? <Typography component="span" sx={{ color: 'red' }}>*</Typography>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <RadioGroup
                                            row
                                            value={formData?.creditAccountInfo?.isTrustee}
                                            onChange={e => handleTrusteeChange(e.target.value)}
                                        >
                                            <FormControlLabel value="yes" control={<Radio size="small" color="primary" disabled={isCreditAccountDisabled} />} label="Yes" sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }} disabled={isCreditAccountDisabled} />
                                            <FormControlLabel value="no" control={<Radio size="small" color="primary" disabled={isCreditAccountDisabled} />} label="No" sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }} disabled={isCreditAccountDisabled} />
                                        </RadioGroup>
                                    </Grid>
                                </Grid>

                                {/* Name of Directors Section */}
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={6} md={12}>
                                        <Input
                                            label="Name of Director/s or Proprietors"
                                            value={formData?.creditAccountInfo?.directorName}
                                            onChange={(e) =>
                                                handleInputChange('creditAccountInfo', 'directorName', e.target.value)
                                            }
                                            onBlur={(e) =>
                                                handleInputChange('creditAccountInfo', 'directorName', toTitleCase(e.target.value))
                                            }
                                            fullWidth
                                            size="small"
                                            multiline
                                            rows={3}
                                            required
                                            disabled={isCreditAccountDisabled}
                                            error={
                                                (isCreditRequired && isEmpty(formData?.creditAccountInfo?.directorName)) ||
                                                !!getCreditAccountDirectorNameValidationMessage()
                                            }
                                            helperText={
                                                isCreditRequired && isEmpty(formData?.creditAccountInfo?.directorName)
                                                    ? 'Director Name is required'
                                                    : getCreditAccountDirectorNameValidationMessage()
                                            }
                                        />
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 3 }} />

                                {/* Credit Referees Section */}
                                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, borderBottom: '2px solid', pb: 1 }}>
                                    Credit Referees Details
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', height: '40px' }}>
                                            Would you wish to provide credit referees? <Typography component="span" sx={{ color: 'red' }}>*</Typography>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <RadioGroup
                                            row
                                            value={formData?.creditAccountInfo?.hasCreditReferees || ''}
                                            onChange={e => handleInputChange('creditAccountInfo', 'hasCreditReferees', e.target.value)}
                                        >
                                            <FormControlLabel value="yes" control={<Radio size="small" color="primary" disabled={isCreditAccountDisabled} />} label="Yes" sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }} disabled={isCreditAccountDisabled} />
                                            <FormControlLabel value="no" control={<Radio size="small" color="primary" disabled={isCreditAccountDisabled} />} label="No" sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }} disabled={isCreditAccountDisabled} />
                                        </RadioGroup>
                                    </Grid>
                                </Grid>
                                {formData?.creditAccountInfo?.hasCreditReferees === "no" ? null : (
                                    formData?.creditReferees.map((referee, index) => (
                                        <Box key={referee.id} sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'primary.dark' }}>
                                                Referee {index + 1}
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Input
                                                        label="Contact Name"
                                                        value={referee.contactName}
                                                        onChange={(e) => handleInputChange('creditReferees', `${referee.id}.contactName`, e.target.value)}
                                                        onBlur={(e) => handleInputChange('creditReferees', `${referee.id}.contactName`, toTitleCase(e.target.value))}
                                                        fullWidth
                                                        size="small"
                                                        required
                                                        disabled={isCreditAccountDisabled}
                                                        error={showValidationErrors && formData.invoiceStatementInfo.needCreditAccount === 'yes' && formData?.creditAccountInfo?.hasCreditReferees === 'yes' && (!referee.contactName || String(referee.contactName).trim() === '')}
                                                        helperText={showValidationErrors && formData.invoiceStatementInfo.needCreditAccount === 'yes' && formData?.creditAccountInfo?.hasCreditReferees === 'yes' && (!referee.contactName || String(referee.contactName).trim() === '') ? 'Contact Name is required' : ''}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Input
                                                        label="Company"
                                                        value={referee.company}
                                                        onChange={(e) => handleInputChange('creditReferees', `${referee.id}.company`, e.target.value)}
                                                        fullWidth
                                                        size="small"
                                                        required
                                                        disabled={isCreditAccountDisabled}
                                                        error={showValidationErrors && formData.invoiceStatementInfo.needCreditAccount === 'yes' && formData?.creditAccountInfo?.hasCreditReferees === 'yes' && (!referee.company || String(referee.company).trim() === '')}
                                                        helperText={showValidationErrors && formData.invoiceStatementInfo.needCreditAccount === 'yes' && formData?.creditAccountInfo?.hasCreditReferees === 'yes' && (!referee.company || String(referee.company).trim() === '') ? 'Company is required' : ''}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Input
                                                        label="Phone Number"
                                                        value={referee.phoneNumber}
                                                        onChange={(e) => {
                                                            const value = e.target.value;

                                                            if (
                                                                value === '' ||
                                                                (/^[0-9]+$/.test(value) &&
                                                                    value.length <= CONTACT_NUMBER_FIELD_MAX_LENGTH)
                                                            ) {
                                                                handleInputChange(
                                                                    'creditReferees',
                                                                    `${referee.id}.phoneNumber`,
                                                                    value
                                                                );
                                                            }
                                                        }}
                                                        fullWidth
                                                        size="small"
                                                        required
                                                        disabled={isCreditAccountDisabled}
                                                        error={
                                                            showValidationErrors &&
                                                            formData.invoiceStatementInfo.needCreditAccount === 'yes' &&
                                                            formData?.creditAccountInfo?.hasCreditReferees === 'yes' &&
                                                            !!getCreditRefereePhoneValidationMessage(referee.phoneNumber)
                                                        }
                                                        helperText={
                                                            showValidationErrors &&
                                                                formData.invoiceStatementInfo.needCreditAccount === 'yes' &&
                                                                formData?.creditAccountInfo?.hasCreditReferees === 'yes'
                                                                ? getCreditRefereePhoneValidationMessage(referee.phoneNumber)
                                                                : ''
                                                        }
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Input
                                                        label="Email Address"
                                                        value={referee.email}
                                                        onChange={(e) => handleInputChange('creditReferees', `${referee.id}.email`, e.target.value)}
                                                        fullWidth
                                                        type="email"
                                                        size="small"
                                                        required
                                                        disabled={isCreditAccountDisabled}
                                                        error={showValidationErrors && formData.invoiceStatementInfo.needCreditAccount === 'yes' && formData?.creditAccountInfo?.hasCreditReferees === 'yes' && (!referee.email || String(referee.email).trim() === '')}
                                                        helperText={showValidationErrors && formData.invoiceStatementInfo.needCreditAccount === 'yes' && formData?.creditAccountInfo?.hasCreditReferees === 'yes' && (!referee.email || String(referee.email).trim() === '') ? 'Email Address is required' : ''}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    ))
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />

                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={12}>
                        {/* Declaration Section */}
                        <Box sx={{
                            height: '100%',
                            background: 'rgba(255,255,255,0.97)',
                            borderRadius: 1,
                            p: { xs: 1, md: 3 },
                            boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.2),1px 0px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
                            border: '1px solid #e0e0e0',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>Declaration</Typography>
                            </Box>
                            <Divider sx={{ mb: 4, borderWidth: 1, borderStyle: 'solid', borderColor: '#003883' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
                                I hereby authorise Eurofins to investigate the listed ABN pertinent to my credit worthiness. I agree to abide by the  of Eurofins. I agree to pay Eurofins invoices within payment terms and relevant legal fees in the event of unreasonable non-compliance to payment terms.
                            </Typography>
                            {/* Terms and Conditions Checkbox */}
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.generalInfo.hasAgreedToTermsAndCondition || false}
                                                onChange={(e) => handleInputChange('generalInfo', 'hasAgreedToTermsAndCondition', e.target.checked)}
                                                color="primary"
                                                size="small"
                                                disabled={isFieldsDisabled}
                                            />
                                        }
                                        label="I have read the Eurofins | Environment Testing Terms & Conditions"
                                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '12px', fontWeight: 600 } }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Comments"
                                        value={formData.generalInfo.tandCComments || ''}
                                        onChange={(e) => handleInputChange('generalInfo', 'tandCComments', e.target.value)}
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        size="small"
                                        disabled={isFieldsDisabled}
                                        inputProps={{ maxLength: 200 }}
                                        helperText={`${(formData.generalInfo.tandCComments || '').length}/200`}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={12}>
                        <Box sx={{
                            height: '100%',
                            background: 'rgba(255,255,255,0.97)',
                            borderRadius: 1,
                            p: { xs: 1.5, md: 2 },
                            boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.2),1px 0px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
                            border: '1px solid #e0e0e0',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>Signature & Authorization</Typography>
                            </Box>

                            <Divider sx={{ mb: 2, borderWidth: 1, borderStyle: 'solid', borderColor: '#003883' }} />

                            <Grid container spacing={2} sx={{ minBlockSize: 2 }}>
                                <Grid item xs={12} sm={6}>
                                    <Input
                                        label="Full Name"
                                        value={formData.generalInfo.fullName}
                                        onChange={(e) =>
                                            handleInputChange('generalInfo', 'fullName', e.target.value)
                                        }
                                        fullWidth
                                        size="small"
                                        disabled={isFieldsDisabled}
                                        error={showValidationErrors && !!getFullNameValidationMessage()}
                                        helperText={showValidationErrors ? getFullNameValidationMessage() : ""}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Input
                                        label="Date"
                                        type="date"
                                        value={formData.generalInfo.requestedDate}
                                        onChange={(e) =>
                                            handleInputChange('generalInfo', 'requestedDate', e.target.value)
                                        }
                                        fullWidth
                                        size="small"
                                        disabled={isFieldsDisabled}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{
                                            max: new Date().toISOString().split('T')[0],
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Input
                                        label="Company"
                                        value={formData.generalInfo.company}
                                        onChange={(e) =>
                                            handleInputChange('generalInfo', 'company', e.target.value)
                                        }
                                        fullWidth
                                        size="small"
                                        disabled={isFieldsDisabled}
                                        error={showValidationErrors && !!getCompanyValidationMessage()}
                                        helperText={showValidationErrors ? getCompanyValidationMessage() : ""}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Input
                                        label="Position Title"
                                        value={formData.generalInfo.title}
                                        onChange={(e) =>
                                            handleInputChange('generalInfo', 'title', e.target.value)
                                        }
                                        fullWidth
                                        size="small"
                                        disabled={isFieldsDisabled}
                                        error={showValidationErrors && !!getTitleValidationMessage()}
                                        helperText={showValidationErrors ? getTitleValidationMessage() : ""}
                                    />
                                </Grid>
                            </Grid>
                            <Box
                                sx={{
                                    minHeight: 110,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    backgroundColor: '#fafafa',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    p: 1,
                                    mt: 2,
                                    overflow: 'hidden'
                                }}
                            >
                                {signatureImage ? (
                                    <Box
                                        component="img"
                                        src={signatureImage}
                                        alt="Client signature"
                                        sx={{
                                            maxWidth: '100%',
                                            maxHeight: 120,
                                            objectFit: 'contain'
                                        }}
                                    />
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No signature available
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />
                {/* Combined ELVIS Client Details and Check List */}

                {isOfficeAdmin && !isUserVersion && (
                    <Card sx={{ boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.2),1px 0px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)', mb: 4 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Grid container spacing={2} alignItems="flex-start">
                                <Grid item xs={12} md={4}>
                                    <Typography variant="body1" sx={{
                                        fontWeight: 500,
                                        display: 'flex',
                                        alignItems: 'center',
                                        height: '40px',
                                        color: 'text.primary'
                                    }}>
                                        Please select the ASM point of contact by state and send an email.
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="share-asm-state-label" sx={{ fontSize: '12px' }}>Select State</InputLabel>
                                        <Select
                                            labelId="share-asm-state-label"
                                            value={selectedAsmState}
                                            label="Select State"
                                            onChange={(e) => {
                                                const nextState = String(e.target.value);
                                                setSelectedAsmState(nextState);
                                            }}
                                            disabled={isDisabled}
                                            sx={{ fontSize: '12px' }}
                                            MenuProps={{ PaperProps: { style: { maxHeight: 240, overflowY: 'auto' } } }}
                                        >
                                            {australianStates.map((option) => (
                                                <MenuItem
                                                    key={option.id}
                                                    value={option.id}
                                                    sx={{ fontSize: '12px' }}
                                                >
                                                    {option.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Autocomplete
                                        multiple
                                        options={stateAsmOptions}
                                        getOptionLabel={(option) => `${option.bumaName || ''}`.trim()}
                                        value={emailList || []}
                                        onChange={(event, newValue) => {
                                            setEmailList(newValue);
                                        }}
                                        disabled={isDisabled}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select ASM"
                                                placeholder="Search and select..."
                                                size="small"
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <li {...props}>
                                                <Box>
                                                    <Typography variant="body2">
                                                        {option.bumaName}
                                                    </Typography>
                                                </Box>
                                            </li>
                                        )}
                                        isOptionEqualToValue={(option, value) => option.bumaEmail === value.bumaEmail}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSubmitForApproval}
                                        sx={{
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            backgroundColor: 'primary.main',
                                        }}
                                        disabled={emailList.length === 0}
                                    >
                                        Send Email
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                )}

                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    width: '100%',
                    p: 0,
                    gap: 0.2,
                    mb: 2,
                    minHeight: 340,
                }}>
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} md={isASMGridSize}>
                            {/* Left Side - ELVIS Client Details */}
                            <Box sx={{
                                flex: 1,
                                background: 'rgba(255,255,255,0.97)',
                                borderRadius: 1,
                                p: { xs: 1, md: 3 },
                                boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.2),1px 0px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
                                border: '1px solid #e0e0e0',
                                minHeight: 300,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>ELVIS Client Details</Typography>
                                </Box>
                                <Divider sx={{ mb: 4, borderWidth: 1, borderStyle: 'solid', borderColor: '#003883' }} />
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Autocomplete
                                            options={newPriceList || []}
                                            getOptionLabel={option => option?.name || ''}
                                            value={(Array.isArray(newPriceList) ? newPriceList.find(opt => opt.id === Number(getResolvedPriceListId(formData?.elvisClientDetails?.priceList))) : null) || null}
                                            onChange={(e, value) => handleInputChange('elvisClientDetails', 'priceList', value ? Number(value.id) : getDefaultPriceListId())}
                                            renderInput={params => (
                                                <TextField
                                                    {...params}
                                                    label="Price List"
                                                    size="small"
                                                    error={showValidationErrors && !formData?.elvisClientDetails?.priceList}
                                                    helperText={showValidationErrors && !formData?.elvisClientDetails?.priceList ? 'Price List is required' : ''}
                                                    disabled={isDisabled}
                                                />
                                            )}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            disabled={isDisabled}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth size="small" error={showValidationErrors && !formData?.elvisClientDetails?.state}>
                                            <InputLabel sx={{ fontSize: '12px' }}>Report State</InputLabel>
                                            <Select value={formData?.elvisClientDetails?.state || ''} label="Report State" onChange={(e) => handleSelectChange('state', e)} disabled={isDisabled} sx={{ fontSize: '12px' }}>
                                                {australianStates.map((option) => (<MenuItem key={option.id} value={option.id} sx={{ fontSize: '12px' }}>{option.name}</MenuItem>))}
                                            </Select>
                                            {showValidationErrors && !formData?.elvisClientDetails?.state && (
                                                <Typography variant="caption" color="error">Report State is required</Typography>
                                            )}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl
                                            fullWidth
                                            size="small"
                                            error={
                                                showValidationErrors &&
                                                (formData.elvisClientDetails.asm === '' ||
                                                    formData.elvisClientDetails.asm === undefined)
                                            }
                                        >
                                            <InputLabel id="asm-label" sx={{ fontSize: '12px' }}>
                                                ASM
                                            </InputLabel>
                                            <Select
                                                labelId="asm-label"
                                                value={formData.elvisClientDetails.asm || ''}
                                                label="ASM"
                                                required
                                                onChange={(e) => handleSelectChange('asm', e)}
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: { maxHeight: 240, overflowY: 'auto' },
                                                    },
                                                }}
                                                disabled={isDisabled}
                                                sx={{ fontSize: '12px' }}
                                            >
                                                {asmOptions?.length > 0 ? (
                                                    asmOptions.map((option) => (
                                                        <MenuItem
                                                            key={option.userID}
                                                            value={option.userID}
                                                            sx={{ fontSize: '12px' }}
                                                        >
                                                            {option.asmName}
                                                        </MenuItem>
                                                    ))
                                                ) : (
                                                    <MenuItem disabled sx={{ fontSize: '12px' }}>
                                                        No data available
                                                    </MenuItem>
                                                )}
                                            </Select>

                                            {showValidationErrors &&
                                                (formData?.elvisClientDetails?.asm === '' ||
                                                    formData?.elvisClientDetails?.asm === undefined) && (
                                                    <Typography variant="caption" color="error">
                                                        ASM is required
                                                    </Typography>
                                                )}
                                        </FormControl>

                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth size="small" error={showValidationErrors && !formData?.elvisClientDetails?.ele}>
                                            <InputLabel id="ele-label" sx={{ fontSize: '12px' }}>ELE</InputLabel>
                                            <Select labelId="ele-label" value={formData?.elvisClientDetails?.ele} label="ELE" onChange={(e) => handleSelectChange('ele', e)} MenuProps={{ PaperProps: { style: { maxHeight: 240, overflowY: 'auto' } } }} disabled={isDisabled} sx={{ fontSize: '12px' }}>
                                                {eleCodes?.map((option) => (<MenuItem key={option} value={option} sx={{ fontSize: '12px' }}>{option}</MenuItem>))}
                                            </Select>
                                            {showValidationErrors && !formData?.elvisClientDetails?.ele && (
                                                <Typography variant="caption" color="error">ELE is required</Typography>
                                            )}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Autocomplete
                                            options={buCodes || []}
                                            getOptionLabel={option => option}
                                            value={formData?.elvisClientDetails?.bu || null}
                                            onChange={(e, value) => handleInputChange('elvisClientDetails', 'bu', value || '')}
                                            renderInput={params => (
                                                <TextField
                                                    {...params}
                                                    label="BU"
                                                    size="small"
                                                    error={showValidationErrors && (formData?.elvisClientDetails?.bu === '' || formData?.elvisClientDetails?.bu === undefined)}
                                                    helperText={showValidationErrors && (formData?.elvisClientDetails?.bu === '' || formData?.elvisClientDetails?.bu === undefined) ? 'BU is required' : ''}
                                                    disabled={isDisabled}
                                                />
                                            )}
                                            isOptionEqualToValue={(option, value) => option === value}
                                            disabled={isDisabled}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                        {isOfficeAdmin && (<Grid item xs={12} md={6}>
                            {/* Right Side - AR Review */}
                            <Box sx={{
                                flex: 1,
                                background: 'rgba(255,255,255,0.97)',
                                borderRadius: 1,
                                p: { xs: 1, md: 3 },
                                boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.2),1px 0px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
                                border: '1px solid #e0e0e0',
                                minHeight: 300,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>AR Review</Typography>
                                </Box>
                                <Divider sx={{ mb: 4, borderWidth: 1, borderStyle: 'solid', borderColor: '#003883' }} />
                                {isOfficeAdmin ? (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Input
                                                label="Client Code"
                                                value={formData?.elvisClientDetails?.code || ''}
                                                onChange={(e) => handleInputChange('elvisClientDetails', 'code', e.target.value)}
                                                onBlur={async (e) => {
                                                    const trimmedValue = e.target.value.trim();
                                                    handleInputChange('elvisClientDetails', 'code', trimmedValue);

                                                    // Call API to validate MYOB code
                                                    await validateMyobCode(trimmedValue);
                                                }}
                                                fullWidth
                                                size="small"
                                                required
                                                inputProps={{ maxLength: 15 }}
                                                sx={{
                                                    '.MuiFormHelperText-root.Mui-error': { marginLeft: 0 },
                                                    fontSize: '12px'
                                                }}
                                                error={myObCodeExists.status || (showValidationErrors && (!formData?.elvisClientDetails?.code || String(formData?.elvisClientDetails?.code).trim() === '' || String(formData?.elvisClientDetails?.code).length > 15))}
                                                helperText={
                                                    showValidationErrors && (!formData?.elvisClientDetails?.code || String(formData?.elvisClientDetails?.code).trim() === '')
                                                        ? 'Client Code is required'
                                                        : showValidationErrors && String(formData?.elvisClientDetails?.code).length > 15
                                                            ? 'Client Code must be 15 characters or less'
                                                            : (myObCodeExists.status ? myObCodeExists.message : '')
                                                }
                                                disabled={isDisabled}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth size="small" error={showValidationErrors && (formData?.elvisClientDetails?.paymentTerms === '' || formData?.elvisClientDetails?.paymentTerms === undefined || formData?.elvisClientDetails?.paymentTerms === null)}>
                                                <InputLabel sx={{ fontSize: '12px' }}>Payment Terms</InputLabel>
                                                <Select value={formData?.elvisClientDetails?.paymentTerms ?? ''} label="Payment Terms" onChange={(e) => handleSelectChange('paymentTerms', e)} disabled={isDisabled} sx={{ fontSize: '12px' }}>
                                                    {paymentTermsOptions.map((option) => (<MenuItem key={option.value} value={option.value} sx={{ fontSize: '12px' }}>{option.description}</MenuItem>))}
                                                </Select>
                                                {showValidationErrors && (formData?.elvisClientDetails?.paymentTerms === '' || formData?.elvisClientDetails?.paymentTerms === undefined || formData?.elvisClientDetails?.paymentTerms === null) && (
                                                    <Typography variant="caption" color="error">Payment Terms is required</Typography>
                                                )}
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel sx={{ fontSize: '12px' }}>Country</InputLabel>
                                                <Select
                                                    value={getElvisCountryValue(formData?.elvisClientDetails?.country)}
                                                    label="Country"
                                                    onChange={(e) => handleSelectChange('country', e)}
                                                    disabled={isDisabled}
                                                    sx={{ fontSize: '12px' }}
                                                >
                                                    {countryOptions.map((option) => (
                                                        <MenuItem key={option} value={option} sx={{ fontSize: '12px' }}>
                                                            {option}
                                                        </MenuItem>))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth size="small" required={!isPaymentTermsZero} error={showValidationErrors && !isBankLetterValid}>
                                                <InputLabel sx={{ fontSize: '12px' }}>Bank Letters</InputLabel>
                                                <Select
                                                    value={formData?.elvisClientDetails?.bankLetterFileName}
                                                    label="Bank Letters"
                                                    onChange={(e) => handleSelectChange('bankLetterFileName', e)}
                                                    disabled={isDisabled}
                                                    sx={{ fontSize: '12px' }}
                                                >
                                                    {bankLetterOptions.map((option) => (
                                                        <MenuItem key={option} value={option} sx={{ fontSize: '12px' }}>
                                                            {option}
                                                        </MenuItem>))}
                                                </Select>
                                                {showValidationErrors && !isBankLetterValid && (
                                                    <Typography variant="caption" color="error">Bank Letters is required</Typography>
                                                )}
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box display="flex" flexDirection="column" alignItems="flex-start">
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={formData?.invoiceStatementInfo?.isPoNumberMandatory === 'yes'}
                                                            onChange={(e) => handleRadioChange('isPoNumberMandatory', e.target.checked ? 'yes' : 'no')}
                                                            disabled={true}
                                                        />
                                                    }
                                                    label="Is PO Required"
                                                    disabled={true}
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box display="flex" flexDirection="column" alignItems="flex-start">
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={formData.elvisClientDetails.country === 'Australia' || !formData.elvisClientDetails.country ? false : true}
                                                            disabled
                                                        />
                                                    }
                                                    label="Is GST Exempt"
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box display="flex" flexDirection="column" alignItems="flex-start">
                                                <FormControlLabel
                                                    control={<Checkbox checked={formData.elvisClientDetails.isEurofinsGroup} disabled={isDisabled} onChange={(e) => handleInputChange('elvisClientDetails', 'isEurofinsGroup', e.target.checked)} />}
                                                    label="Is Eurofins Group"
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box display="flex" flexDirection="column" alignItems="flex-start">
                                                <FormControlLabel
                                                    control={<Checkbox checked={formData.elvisClientDetails.abnCheck} onChange={(e) => handleInputChange('elvisClientDetails', 'abnCheck', e.target.checked)} disabled={isDisabled} />}
                                                    label="ABN Checked"
                                                />
                                                {showValidationErrors && !formData.elvisClientDetails.abnCheck && String(formData.elvisClientDetails.paymentTerms) !== '0' && (
                                                    <Typography variant="caption" color="error" sx={{ ml: 4, mt: -1 }}>ABN Check is mandatory</Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box display="flex" flexDirection="column" alignItems="flex-start">
                                                <FormControlLabel
                                                    control={<Checkbox checked={formData.elvisClientDetails.refereeCheck} disabled={isDisabled} onChange={(e) => handleInputChange('elvisClientDetails', 'refereeCheck', e.target.checked)} />}
                                                    label="Referee Checked"
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box display="flex" flexDirection="column" alignItems="flex-start">
                                                <FormControlLabel
                                                    control={<Checkbox checked={formData?.elvisClientDetails?.isInvoiceMonthly} onChange={(e) => handleInputChange('elvisClientDetails', 'isInvoiceMonthly', e.target.checked)} disabled={isDisabled} />}
                                                    label="Is Invoice Monthly"
                                                    disabled={isDisabled}
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">Not available</Typography>
                                )}
                            </Box>
                        </Grid>)}
                    </Grid>
                </Box>

                {isOfficeAdmin && (
                    <Card
                        sx={{
                            boxShadow:
                                '0px 2px 4px 1px rgba(0,0,0,0.2),1px 0px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
                            mb: 4,
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={5}
                                label="Notes"
                                placeholder="Enter your notes..."
                                value={formData?.generalInfo?.reviewerNotes}
                                onChange={(e) => {
                                    if (e.target.value.length <= 100) {
                                        setFormData({
                                            ...formData,
                                            generalInfo: {
                                                ...formData.generalInfo,
                                                reviewerNotes: e.target.value,
                                            },
                                        });
                                    }
                                }}
                                inputProps={{ maxLength: 100 }}
                                disabled={isDisabled}
                                helperText={`${formData?.generalInfo?.reviewerNotes?.length || 0}/100 characters`}
                            />
                        </CardContent>
                    </Card>
                )}

                {/* Action Buttons */}

                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
                    <Button
                        variant="outlined"
                        onClick={() => navigate(`/home/new-client-request`)}
                        sx={{
                            fontWeight: 600,
                            textTransform: 'none',
                            minWidth: '100px',
                        }}
                    >
                        Back
                    </Button>
                    {!isUserVersion && <>
                        {(isASM || isBothPermissions) &&
                            <Button
                                variant="contained"
                                onClick={() => handleConfirmApprove(2)}
                                sx={{
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    backgroundColor: 'primary.main',
                                    minWidth: '100px',
                                }}
                                disabled={canEditElvis ? !isElvisFormValid : isDisabled}
                            >
                                {(!isUserVersion && isASM && !isOfficeAdmin) ? 'Submit' : 'Save'}
                            </Button>}
                        {(isOfficeAdmin || isBothPermissions) && <>
                            <Tooltip title="Re-Draft" arrow>
                                <Button
                                    variant="contained"
                                    onClick={() => handleReDraftClick(data.clientOnboardRequest.id)}
                                    sx={{
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        backgroundColor: 'primary.main',
                                        minWidth: '100px',
                                    }}
                                    disabled={isDisabled || !isOfficeAdmin}
                                >
                                    Re-Draft
                                </Button>
                            </Tooltip>
                            <Dialog open={showReDraftDialog} onClose={() => setShowReDraftDialog(false)} maxWidth="sm" fullWidth>
                                <DialogTitle>Re-Draft Request</DialogTitle>
                                <DialogContent>
                                    <Typography sx={{ mb: 2 }}>
                                        Please provide a reason for re-drafting this request:
                                    </Typography>
                                    <Input
                                        label="Re-Draft Reason"
                                        value={reDraftReason}
                                        onChange={(e) => setReDraftReason(e.target.value)}
                                        fullWidth
                                        multiline
                                        rows={3}
                                        required
                                        size="small"
                                    />
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setShowReDraftDialog(false)}>Cancel</Button>
                                    <Button
                                        onClick={handleReDraftSubmit}
                                        variant="contained"
                                        color="primary"
                                        disabled={!reDraftReason.trim() || isDisabled}
                                    >
                                        Submit
                                    </Button>
                                </DialogActions>
                            </Dialog>
                            <Button
                                variant="contained"
                                onClick={() => handleApproveClick()}
                                sx={{
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    backgroundColor: 'primary.main',
                                    minWidth: '100px',
                                }}
                                disabled={isDisabled || !isOfficeAdmin}
                            >
                                Approve
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={() => handleReject(false)}
                                sx={{
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    minWidth: '100px',
                                }}
                                disabled={isDisabled || !isOfficeAdmin}
                            >
                                Reject
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => handleReject(true)}
                                sx={{
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    minWidth: '100px',
                                }}
                                disabled={isDisabled || !isOfficeAdmin}
                            >
                                Defer
                            </Button>
                        </>}
                    </>}
                </Box>

                <ConfirmationDialog
                    open={showConfirmDialog}
                    onClose={() => setShowConfirmDialog(false)}
                    onConfirm={() => handleConfirmApprove(3)}
                    title="Confirm Approval"
                    message="This will proceed to register the Client information in ELVIS - are you sure you want to continue?"
                />

                {/* Approval Confirmation Dialog */}
                <Dialog open={showApprovalDialog} onClose={() => setShowApprovalDialog(false)} maxWidth="md" fullWidth>
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: 'success.main' }} />
                        Please verify before proceeding with Approval
                    </DialogTitle>
                    <DialogContent sx={{ pb: 2 }}>

                        {/* To: Email Recipients Section */}
                        {emailList.length > 0 && (
                            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                                    To:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {emailList.map((asm, index) => {
                                        const displayName = `${asm.bumaName}`;
                                        return (
                                            <Chip
                                                key={index}
                                                label={displayName}
                                                onDelete={() => {
                                                    handleDeleteEmail(asm);
                                                    // Close dialog if this was the last email
                                                    if (emailList.length === 1) {
                                                        setShowApprovalDialog(false);
                                                    }
                                                }}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                sx={{ m: 0.25 }}
                                            />
                                        );
                                    })}
                                </Box>
                            </Box>
                        )}

                        {/* ELVIS Client Details Summary */}
                        <Card variant="outlined" sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'secondary.main' }}>
                                    ELVIS Client Details
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2"><strong>Business Name:</strong> {formData.generalInfo.companyName || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2"><strong>Contact Name:</strong> {formData.generalInfo.contactName || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2"><strong>Email:</strong> {formData.generalInfo.emailAddress || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2"><strong>Price List:</strong> {getPriceListLabel(formData.elvisClientDetails.priceList)}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2"><strong>ASM:</strong> {getASMLabel(formData.elvisClientDetails.asm)}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2"><strong>ELE:</strong> {getEleLabel(formData.elvisClientDetails.ele) || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2"><strong>BU:</strong> {getBULabel(formData.elvisClientDetails.bu)}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* AR Review Summary */}
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'secondary.main' }}>
                                    AR Review
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2"><strong>Client Code:</strong> {formData.elvisClientDetails.code || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2"><strong>Payment Terms:</strong> {getPaymentTermsLabel(formData.elvisClientDetails.paymentTerms)}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2"><strong>Report State:</strong> {getStateLabel(formData.elvisClientDetails.state)}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2"><strong>Is PO Required:</strong> {formData.elvisClientDetails.isPoRequired ? 'Yes' : 'No'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2"><strong>Is Eurofins Group:</strong> {formData.elvisClientDetails.isEurofinsGroup ? 'Yes' : 'No'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2"><strong>Is GST Exempt:</strong> {formData.elvisClientDetails.isGstExempt ? 'Yes' : 'No'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2">
                                            <strong>ABN Check:</strong> {formData.elvisClientDetails.abnCheck ? 'Yes' : 'No'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2">
                                            <strong>Referee Check:</strong> {
                                                formData.elvisClientDetails.refereeCheck ? 'Yes' : 'No'
                                            }
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2"><strong>Is Invoice Monthly:</strong> {formData.elvisClientDetails.isInvoiceMonthly ? 'Yes' : 'No'}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                        <Box sx={{ mt: 2 }}>
                            <div id="textEditorId">
                                <ReactQuill
                                    onChange={handleEditorChange}
                                    value={content}
                                    theme={"snow"}
                                    modules={modules}
                                />
                            </div>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={() => setShowApprovalDialog(false)}>Cancel</Button>
                        <Button onClick={sendApproval} variant="contained" color="primary">
                            Send
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Rejection Dialog */}
                <Dialog open={showRejectDialog} onClose={() => {
                    setShowRejectDialog(false);
                    handleInputChange('elvisClientDetails', 'reviewerComments', '');
                }} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CancelIcon sx={{ color: 'error.main' }} />
                        Please provide reason
                    </DialogTitle>
                    <DialogContent>
                        <Typography sx={{ mb: 2 }}>
                            {isDefferred
                                ? 'Please provide a reason for deferring this request:'
                                : 'Please provide a reason for rejecting this request:'}
                        </Typography>
                        <Input
                            label="Reason"
                            value={formData.elvisClientDetails.reviewerComments}
                            onChange={(e) => handleInputChange('elvisClientDetails', 'reviewerComments', e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            required
                            size="small"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={() => {
                            setShowRejectDialog(false);
                            handleInputChange('elvisClientDetails', 'reviewerComments', '');
                        }}>Cancel</Button>
                        <Button
                            onClick={confirmReject}
                            variant="contained"
                            color="error"
                            disabled={!formData.elvisClientDetails.reviewerComments.trim()}
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Success Alert */}
                <Snackbar
                    open={showSuccessAlert}
                    autoHideDuration={6000}
                    onClose={() => setShowSuccessAlert(false)}
                >
                    <Alert
                        severity={showValidationErrors ? "error" : "success"}
                        onClose={() => setShowSuccessAlert(false)}
                        sx={{ width: '100%' }}
                    >
                        {alertMessage}
                    </Alert>
                </Snackbar>
                <Snackbar
                    open={apiError.status}
                    autoHideDuration={6000}
                    onClose={() => setApiError({ ...apiError, status: false })}
                >
                    <Alert
                        severity={apiError.type}
                        onClose={() => setApiError({ ...apiError, status: false })}
                        sx={{ width: '100%' }}
                    >
                        {apiError.message}
                    </Alert>
                </Snackbar>

                <PrintComponent
                    formData={formData}
                    asmOptions={asmOptions}
                    eleCodes={eleCodes}
                    buCodes={buCodes}
                    newPriceList={newPriceList}
                    paymentTermsOptions={paymentTermsOptions}
                    australianStates={australianStates}
                    countryOptions={countryOptions}
                    signatureImage={signatureImage}
                />
            </Box>
        </>
    );
};

export default ReviewConfirmation;